// src/modules/captazap/components/QueueList.jsx
// Visual compacto v31.6 · Full v67 — Fila de Proteção com identidade visual por origem:
// Dra. Malu = violeta; sem resposta +3h = rose/vermelho; retorno após fechamento = âmbar.
// Em recuperação fica no Follow-up; recuperado vai para Fila; IA conta como respondido.

import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  FaUserCircle,
  FaStar,
  FaWhatsapp,
  FaFacebookMessenger,
  FaTelegramPlane,
  FaInbox,
  FaInstagram,
} from "react-icons/fa";
import {
  Search,
  SlidersHorizontal,
  MessageCircle,
  Clock3,
  Users,
  Radar,
  Handshake,
  CalendarCheck,
  BadgeCheck,
  UserX,
  Trophy,
  Ban,
  RotateCcw,
  ArrowLeftRight,
  CircleDollarSign,
  UserRound,
  Sparkles,
  Tag,
  Archive,
  BellRing,
  CheckCheck,
  Headphones,
  ShieldAlert,
  Flame,
} from "lucide-react";

const TAB_CONFIG = [
  {
    key: "minhas",
    label: "Minhas",
    icon: MessageCircle,
    empty: "Nenhuma conversa em atendimento.",
    activeClass:
      "from-blue-600 via-blue-700 to-blue-900 text-white shadow-blue-900/20",
    badgeClass: "bg-blue-100 text-blue-700",
  },
  {
    key: "fila",
    label: "Fila",
    icon: Clock3,
    empty: "Nenhuma conversa aguardando.",
    activeClass:
      "from-amber-400 via-orange-500 to-orange-600 text-white shadow-orange-900/20",
    badgeClass: "bg-amber-100 text-amber-700",
  },
  {
    key: "contatos",
    label: "Contatos",
    icon: Users,
    empty: "Nenhum contato encontrado.",
    activeClass:
      "from-emerald-500 via-emerald-600 to-emerald-700 text-white shadow-emerald-900/20",
    badgeClass: "bg-emerald-100 text-emerald-700",
  },
];

const DRA_MALU_AVATAR_URL = "/dra-malu-avatar.webp";
const DRA_MALU_EM_RECUPERACAO_BADGE = "Dra. Malu · Em recuperação";
const DRA_MALU_EM_RECUPERACAO_TITULO =
  "Este contato está sendo trabalhado automaticamente pela Dra. Malu.";
const DRA_MALU_RECUPERADO_BADGE = "🟡 Recuperado Dra. Malu";
const DRA_MALU_RECUPERADO_BADGE_CURTO = "Recuperado Dra. Malu";
const DRA_MALU_RECUPERADO_TITULO =
  "Contato recuperado pela Dra. Malu. A responsabilidade agora está com o captador.";

// Multiconexão: identifica visualmente em qual WhatsApp a conversa entrou.
// O selo é exibido apenas quando o operador está na aba "Todas", evitando
// redundância quando uma conexão específica já está selecionada no cabeçalho.
function getConexaoInstanceIdCard(conversa = {}) {
  return String(
    conversa?.zapi_instance_id ||
      conversa?.zapiInstanceId ||
      conversa?.conexao_instance_id ||
      conversa?.conexaoInstanceId ||
      conversa?.instance_id ||
      conversa?.instanceId ||
      conversa?.chamada?.zapi_instance_id ||
      conversa?.chamado?.zapi_instance_id ||
      conversa?.mensagem?.zapi_instance_id ||
      "",
  ).trim();
}

function getNomeConexaoWhatsappCard(conversa = {}, conexoes = []) {
  const instanceId = getConexaoInstanceIdCard(conversa);
  if (!instanceId) return "";

  const conexao = (Array.isArray(conexoes) ? conexoes : []).find((item) => {
    const itemInstanceId = String(
      item?.zapi_instance_id ||
        item?.zapiInstanceId ||
        item?.instance_id ||
        item?.instanceId ||
        "",
    ).trim();

    return itemInstanceId && itemInstanceId === instanceId;
  });

  return String(
    conexao?.nome ||
      conexao?.nome_exibicao ||
      conexao?.nomeExibicao ||
      conexao?.nome_aba ||
      conexao?.nomeAba ||
      conexao?.label ||
      "",
  ).trim();
}

function isCanalWhatsappCard(canal = "") {
  const valor = String(canal || "")
    .trim()
    .toLowerCase();
  return !valor || valor === "whatsapp" || valor === "wa" || valor.includes("whats");
}

function normalizarTelefoneFixacaoQueueList(valor = "") {
  const digits = normalizarTelefone(valor);
  if (!digits) return "";
  if (digits.startsWith("55") && digits.length >= 12) return digits;
  if (digits.length === 10 || digits.length === 11) return `55${digits}`;
  return digits;
}

function getChaveConversaFixadaQueueList(conversa = {}) {
  const instancia = getConexaoInstanceIdCard(conversa);
  const telefone = normalizarTelefoneFixacaoQueueList(
    conversa?.telefone ||
      conversa?.numero ||
      conversa?.phone ||
      conversa?.whatsapp ||
      conversa?.telefone_normalizado ||
      conversa?.lead?.telefone1 ||
      conversa?.lead?.telefone ||
      "",
  );

  return instancia && telefone ? `${instancia}::${telefone}` : "";
}

function getFixacaoConversaQueueList(conversa = {}, fixacoesPorChave = new Map()) {
  const chave = getChaveConversaFixadaQueueList(conversa);
  return chave ? fixacoesPorChave.get(chave) || null : null;
}

function isFixacaoDoUsuarioQueueList(fixacao = null, usuarioId = null, usuarioNome = "") {
  if (!fixacao) return false;

  const fixacaoUsuarioId = String(fixacao?.usuario_id || fixacao?.usuarioId || "").trim();
  const usuarioIdAtual = String(usuarioId || "").trim();
  if (fixacaoUsuarioId && usuarioIdAtual) return fixacaoUsuarioId === usuarioIdAtual;

  const nomeFixacao = normalizar(fixacao?.usuario_nome || fixacao?.usuarioNome || "");
  const nomeAtual = normalizar(usuarioNome || "");
  return Boolean(nomeFixacao && nomeAtual && nomeFixacao === nomeAtual);
}

function isSomenteContatoCaptaZapCard(conversa = {}) {
  const leadDireta =
    conversa?.lead && typeof conversa.lead === "object"
      ? getLeadIdGenericoCard(conversa.lead)
      : null;
  const leadVinculada = getLeadsVinculadasCard(conversa).some((lead) =>
    Boolean(getLeadIdGenericoCard(lead)),
  );
  const leadId = getPrimeiroTexto([
    conversa?.lead_id,
    conversa?.leadId,
    conversa?.id_lead,
    conversa?.lead_contexto_id,
    conversa?.leadContextoId,
    conversa?.contexto_lead_id,
    conversa?._captazap_chamado_lead_id,
    leadDireta,
  ]);

  return !leadId && !leadVinculada;
}

function normalizarContatoCRMParaBuscaQueueList(item = {}) {
  if (!item || typeof item !== "object") return item;

  const pareceLeadCRM = Boolean(
    item.nome_lead ||
    item.nomeLead ||
    item.telefone1 ||
    item.telefone2 ||
    item.status_negociacao ||
    item.status_agendamento ||
    item.data_lancamento ||
    item.data_lead ||
    item.usuario_criador_id ||
    item.id_usuario ||
    item.id_fonte_lead ||
    item.id_campanha,
  );

  if (!pareceLeadCRM) return item;

  const leadOriginal =
    item.lead && typeof item.lead === "object" ? item.lead : item;
  const leadId =
    getLeadIdGenericoCard(leadOriginal) ||
    item.lead_id ||
    item.leadId ||
    item.id_lead ||
    item.id ||
    null;
  const nome =
    item.nome ||
    item.nome_lead ||
    item.nomeLead ||
    item.nome_cliente ||
    item.paciente ||
    leadOriginal.nome_lead ||
    leadOriginal.nome ||
    leadOriginal.nomeLead ||
    (leadId ? `Lead ${leadId}` : "Contato CRM");
  const telefone =
    item.numero ||
    item.telefone ||
    item.telefone1 ||
    item.telefone2 ||
    item.celular ||
    item.whatsapp ||
    leadOriginal.telefone1 ||
    leadOriginal.telefone2 ||
    leadOriginal.telefone ||
    leadOriginal.numero ||
    "";

  const leadNormalizada = {
    ...leadOriginal,
    id: leadId || leadOriginal.id,
    nome_lead: leadOriginal.nome_lead || nome,
    nome: leadOriginal.nome || nome,
    telefone1: leadOriginal.telefone1 || item.telefone1 || telefone,
    telefone2: leadOriginal.telefone2 || item.telefone2 || "",
    empresa_id: leadOriginal.empresa_id || item.empresa_id || item.empresaId,
  };

  const leadsVinculadas = dedupeLeadsVinculadasCard([
    ...getLeadsVinculadasCard(item),
    leadNormalizada,
  ]);

  return {
    ...item,
    id:
      String(item.id || "").startsWith("lead-") || !leadId
        ? item.id || `lead-${normalizarTelefone(telefone) || nome}`
        : `lead-${leadId}`,
    empresa_id: item.empresa_id || item.empresaId || leadNormalizada.empresa_id,
    nome,
    nome_lead: item.nome_lead || leadNormalizada.nome_lead || nome,
    numero: item.numero || telefone,
    telefone: item.telefone || telefone,
    telefone1: item.telefone1 || leadNormalizada.telefone1 || telefone,
    telefone2: item.telefone2 || leadNormalizada.telefone2 || "",
    telefone_normalizado:
      item.telefone_normalizado || normalizarTelefone(telefone),
    lead_id: item.lead_id || leadId,
    leadId: item.leadId || leadId,
    lead_contexto_id: item.lead_contexto_id || item.leadContextoId || leadId,
    leadContextoId: item.leadContextoId || item.lead_contexto_id || leadId,
    lead: item.lead || leadNormalizada,
    leads_vinculadas: leadsVinculadas,
    leadsVinculadas: leadsVinculadas,
    canal: item.canal || "whatsapp",
    status: item.status || "lead_crm",
    _captazap_origem_contatos: item._captazap_origem_contatos ?? true,
    _contato_crm_consulta: item._contato_crm_consulta ?? true,
    _captazap_contexto_fixo_contato:
      item._captazap_contexto_fixo_contato ?? true,
    _captazap_lead_clicada_id: item._captazap_lead_clicada_id || leadId || null,
    _forcar_historico_por_telefone: item._forcar_historico_por_telefone ?? true,
  };
}

function normalizarListaContatosCRMParaBuscaQueueList(lista = []) {
  return (Array.isArray(lista) ? lista : [])
    .filter((item) => item && typeof item === "object")
    .map(normalizarContatoCRMParaBuscaQueueList);
}

function conversaAguardaRespostaCaptadorCard(conversa = {}) {
  if (!conversa || typeof conversa !== "object") return false;
  if (isChamadoFechadoCard(conversa)) return false;

  // A API calcula este sinal usando a última interação OPERACIONAL do chamado.
  // Ele continua correto mesmo quando o último registro cronológico é um
  // lembrete automático. Precisa vir antes do resumo de mensagens, pois a
  // listagem recebe apenas uma mensagem por conversa.
  const sinalOperacional = getSinalPrecisaResponderCard(conversa);
  if (sinalOperacional !== null) return sinalOperacional;

  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];
  const ultimaMensagemStatus = getUltimaMensagemValidaParaStatusCard(
    conversa,
    mensagens,
  );

  // A última interação operacional decide a pendência. A quantidade de
  // mensagens abertas/não abertas não entra nesta regra.
  if (ultimaMensagemStatus?.tipo === "cliente") return true;
  if (ultimaMensagemStatus?.tipo === "clinica") return false;

  const ultimaMensagem =
    getUltimaMensagemConversa(conversa, mensagens) ||
    getUltimaMensagemBrutaConversa(conversa);

  if (ultimaMensagem && isMensagemCliente(ultimaMensagem)) return true;
  if (ultimaMensagem && isMensagemOperador(ultimaMensagem)) return false;

  // Compatibilidade com payloads antigos que trazem apenas a direção/status.
  const direcao = normalizar(
    conversa.ultima_mensagem_direcao ||
      conversa.ultimaMensagemDirecao ||
      conversa.ultima_direcao ||
      conversa.ultimaDirecao ||
      conversa.last_direction ||
      conversa.direcao ||
      conversa.direction ||
      "",
  );

  if (
    direcao.includes("enviad") ||
    direcao.includes("saida") ||
    direcao.includes("outbound")
  ) {
    return false;
  }

  if (
    direcao.includes("recebid") ||
    direcao.includes("entrada") ||
    direcao.includes("inbound")
  ) {
    return true;
  }

  const statusOperacional = normalizar(
    [
      conversa.status_operacional,
      conversa.statusOperacional,
      conversa.badge_status,
      conversa.badgeStatus,
      conversa.acao_recomendada,
      conversa.acaoRecomendada,
    ]
      .filter(Boolean)
      .join(" "),
  );

  if (
    statusOperacional.includes("respondido") ||
    statusOperacional.includes("respondida") ||
    statusOperacional.includes("sem pendencia")
  ) {
    return false;
  }

  return Boolean(
    conversa.precisa_responder === true ||
    conversa.precisa_responder === 1 ||
    String(conversa.precisa_responder || "").toLowerCase() === "true" ||
    conversa.precisaResponder === true ||
    conversa.precisaResponder === 1 ||
    String(conversa.precisaResponder || "").toLowerCase() === "true" ||
    statusOperacional.includes("responder"),
  );
}

function getSinalPrecisaResponderCard(conversa = {}) {
  if (!conversa || typeof conversa !== "object") return null;

  const campos = ["precisa_responder", "precisaResponder"];

  for (const campo of campos) {
    if (!Object.prototype.hasOwnProperty.call(conversa, campo)) continue;

    const valor = conversa[campo];

    if (valor === true || valor === 1) return true;
    if (valor === false || valor === 0) return false;

    const texto = String(valor ?? "")
      .trim()
      .toLowerCase();
    if (["true", "1", "sim", "yes"].includes(texto)) return true;
    if (["false", "0", "nao", "não", "no"].includes(texto)) return false;
  }

  return null;
}

function isContatoCRMBuscaQueueList(item = {}) {
  if (!item || typeof item !== "object") return false;

  return Boolean(
    item._contato_crm_consulta === true ||
      item._captazap_origem_contatos === true ||
      normalizar(item.status || item.status_operacional || "") === "lead crm",
  );
}

function getTelefoneBuscaKeyQueueList(item = {}) {
  if (!item || typeof item !== "object") return "";

  const lead = getLeadPrincipalConversaCard(item) || {};
  const empresaId = item.empresa_id || item.empresaId || lead.empresa_id || "sem-empresa";
  const telefone = normalizarTelefone(
    item.numero ||
      item.telefone ||
      item.telefone1 ||
      item.telefone2 ||
      item.celular ||
      item.whatsapp ||
      item.telefone_normalizado ||
      lead.telefone1 ||
      lead.telefone2 ||
      lead.telefone ||
      lead.numero ||
      "",
  );

  if (!telefone) return "";
  return `${empresaId}-tel-${telefone.slice(-11)}`;
}

function combinarListasBuscaContatosCaptaZap(...listas) {
  const itens = listas.flatMap((lista) =>
    (Array.isArray(lista) ? lista : []).filter(
      (item) => item && typeof item === "object",
    ),
  );

  // A busca da aba Contatos mistura duas naturezas diferentes:
  // 1) registro da carteira CRM (não possui zapi_instance_id);
  // 2) conversa operacional do CaptaZap (possui a conexão/instância).
  //
  // No multiconexão, getConversationKey precisa manter a instância para não
  // juntar duas conversas reais do mesmo telefone em conexões diferentes.
  // Porém essa mesma regra fazia o registro CRM aparecer como um segundo card.
  // Aqui fazemos a deduplicação específica da BUSCA: o CRM enriquece a conversa
  // operacional de mesmo telefone, mas nunca vira um card duplicado.
  const crmPorTelefone = new Map();
  const crmSemTelefone = [];
  const operacionais = [];

  for (const item of itens) {
    if (isContatoCRMBuscaQueueList(item)) {
      const telefoneKey = getTelefoneBuscaKeyQueueList(item);

      if (!telefoneKey) {
        crmSemTelefone.push(item);
        continue;
      }

      const atualCRM = crmPorTelefone.get(telefoneKey);
      crmPorTelefone.set(
        telefoneKey,
        atualCRM ? mesclarContatoBuscaCaptaZap(atualCRM, item) : item,
      );
      continue;
    }

    operacionais.push(item);
  }

  const map = new Map();
  const telefonesComConversa = new Set();

  for (const item of operacionais) {
    const telefoneKey = getTelefoneBuscaKeyQueueList(item);
    const crm = telefoneKey ? crmPorTelefone.get(telefoneKey) : null;
    const itemFinal = crm ? mesclarContatoBuscaCaptaZap(item, crm) : item;
    const key = getConversationKey(itemFinal);
    const atual = map.get(key);

    map.set(
      key,
      atual ? mesclarContatoBuscaCaptaZap(atual, itemFinal) : itemFinal,
    );

    if (telefoneKey) telefonesComConversa.add(telefoneKey);
  }

  // Só mantém um card puramente CRM quando não existe conversa operacional
  // para aquele telefone. Se houver conversa em duas conexões diferentes,
  // preserva as duas conversas (cada uma com sua etiqueta), mas não cria um
  // terceiro card CRM.
  for (const [telefoneKey, item] of crmPorTelefone.entries()) {
    if (telefonesComConversa.has(telefoneKey)) continue;

    const key = getConversationKey(item);
    const atual = map.get(key);
    map.set(key, atual ? mesclarContatoBuscaCaptaZap(atual, item) : item);
  }

  for (const item of crmSemTelefone) {
    const key = getConversationKey(item);
    const atual = map.get(key);
    map.set(key, atual ? mesclarContatoBuscaCaptaZap(atual, item) : item);
  }

  return [...map.values()];
}

function mesclarContatoBuscaCaptaZap(a = {}, b = {}) {
  const leadsA = getLeadsVinculadasCard(a);
  const leadsB = getLeadsVinculadasCard(b);
  const leadsVinculadas = dedupeLeadsVinculadasCard([...leadsA, ...leadsB]);

  const leadContextoId =
    a.lead_contexto_id ||
    a.leadContextoId ||
    b.lead_contexto_id ||
    b.leadContextoId ||
    getLeadIdGenericoCard(getLeadPrincipalConversaCard(a)) ||
    getLeadIdGenericoCard(getLeadPrincipalConversaCard(b)) ||
    null;

  const leadContexto = leadsVinculadas.find(
    (lead) =>
      String(getLeadIdGenericoCard(lead) || "") ===
      String(leadContextoId || ""),
  );

  return {
    ...b,
    ...a,
    nome:
      getNomeLeadVinculadaCard(leadContexto) ||
      a.nome ||
      a.nome_lead ||
      a.nomeLead ||
      b.nome ||
      b.nome_lead ||
      b.nomeLead ||
      "",
    telefone:
      a.telefone ||
      a.numero ||
      a.telefone1 ||
      b.telefone ||
      b.numero ||
      b.telefone1 ||
      "",
    numero:
      a.numero ||
      a.telefone ||
      a.telefone1 ||
      b.numero ||
      b.telefone ||
      b.telefone1 ||
      "",
    lead_contexto_id: leadContextoId,
    leadContextoId: leadContextoId,
    leads_vinculadas: leadsVinculadas,
    leadsVinculadas: leadsVinculadas,
    lead:
      leadContexto ||
      a.lead ||
      b.lead ||
      getLeadPrincipalConversaCard(a) ||
      getLeadPrincipalConversaCard(b) ||
      null,
  };
}

function dedupeLeadsVinculadasCard(lista = []) {
  const seen = new Set();

  return (lista || []).filter((lead) => {
    if (!lead || typeof lead !== "object") return false;

    const key = String(
      getLeadIdGenericoCard(lead) ||
        normalizarTelefone(
          lead?.telefone1 ||
            lead?.telefone2 ||
            lead?.telefone ||
            lead?.celular ||
            lead?.whatsapp ||
            lead?.numero ||
            "",
        ) ||
        getNomeLeadVinculadaCard(lead),
    );

    if (!key) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function QueueList({
  minhasConversas = [],
  filaDeEspera = [],
  pendenciaAgendaConversas = [],
  followUpConversas = [],
  followUpRegras = [],
  followUpCadenciaResumo = "",
  fechadosConversas = [],
  totalFechados = 0,
  contatos = [],
  contatosBuscaGlobal = [],
  buscaOperacionalGlobal = [],
  buscandoOperacaoGlobal = false,
  onBuscarOperacaoGlobal,
  onBuscarContatosCRMGlobal,
  buscandoContatosCRMGlobal = false,
  totalContatosCRM = 0,
  totalContatosVisiveis = 0,
  conversaSelecionada,
  onSelectConversa,
  onChangeAba,
  onCarregarMais,
  paginacaoPorAba = {},
  totaisPorAba = {},
  usuarios = [],
  conexoesWhatsApp = [],
  conexaoSelecionada = "todas",
  conversasFixadas = [],
  usuarioAtualId = null,
  usuarioAtualNome = "",
}) {
  const [aba, setAba] = useState("minhas");
  const [buscaLocal, setBuscaLocal] = useState("");
  const buscaDiferida = useDeferredValue(buscaLocal);
  const [somenteNaoLidas, setSomenteNaoLidas] = useState(false);
  const [agora, setAgora] = useState(() => Date.now());

  const fixacoesPorChave = useMemo(() => {
    const map = new Map();
    for (const item of Array.isArray(conversasFixadas) ? conversasFixadas : []) {
      const chave = getChaveConversaFixadaQueueList(item);
      if (chave) map.set(chave, item);
    }
    return map;
  }, [conversasFixadas]);

  const conversasFixadasSet = useMemo(
    () => new Set(fixacoesPorChave.keys()),
    [fixacoesPorChave],
  );

  const minhasConversasVisiveis = useMemo(() => {
    const lista = Array.isArray(minhasConversas) ? minhasConversas : [];
    return lista.filter((conversa) => {
      const fixacao = getFixacaoConversaQueueList(conversa, fixacoesPorChave);
      const fixadaPorMim = isFixacaoDoUsuarioQueueList(
        fixacao,
        usuarioAtualId,
        usuarioAtualNome,
      );

      // Acompanhamento fixado é uma camada paralela ao responsável do chamado.
      // Mesmo que outro operador assuma/transfira/feche, continua em Minhas de
      // quem fixou até que a própria pessoa desafixe.
      return fixadaPorMim || deveAparecerEmMinhasCaptaZap(conversa);
    });
  }, [
    minhasConversas,
    fixacoesPorChave,
    usuarioAtualId,
    usuarioAtualNome,
  ]);

  useEffect(() => {
    const timer = window.setInterval(() => setAgora(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const contatosEfetivos = useMemo(() => {
    const listaContatos =
      normalizarListaContatosCRMParaBuscaQueueList(contatos);
    const listaGlobal =
      normalizarListaContatosCRMParaBuscaQueueList(contatosBuscaGlobal);

    // Lista visível: mantém o lote limitado para a aba Contatos não ficar lenta.
    if (listaContatos.length > 0) return listaContatos;

    return listaGlobal;
  }, [contatos, contatosBuscaGlobal]);

  const contatosBuscaCRMCompleta = useMemo(() => {
    const listaGlobal =
      normalizarListaContatosCRMParaBuscaQueueList(contatosBuscaGlobal);
    const listaContatos =
      normalizarListaContatosCRMParaBuscaQueueList(contatos);

    // Base de busca: precisa ser a carteira CRM completa, não os 220 renderizados.
    // Normaliza também leads brutas vindas da API, porque algumas versões do pai
    // enviam registros de `leads` diretamente em vez de objetos já convertidos.
    return listaGlobal.length > 0 ? listaGlobal : listaContatos;
  }, [contatos, contatosBuscaGlobal]);

  const fechadosEfetivos = useMemo(
    () => (Array.isArray(fechadosConversas) ? fechadosConversas : []),
    [fechadosConversas],
  );

  const followUpResumoTexto = useMemo(() => {
    const resumoRecebido = String(followUpCadenciaResumo || "").trim();
    return resumoRecebido || formatarResumoFollowUpLocal(followUpRegras);
  }, [followUpCadenciaResumo, followUpRegras]);

  const followUpsPendentesVisiveis = useMemo(() => {
    const lista = Array.isArray(followUpConversas) ? followUpConversas : [];

    return lista.filter((conversa) =>
      isFollowUpVencidoVisivel(conversa, agora),
    );
  }, [followUpConversas, agora]);

  const filaRealVisivel = useMemo(() => {
    const lista = Array.isArray(filaDeEspera) ? filaDeEspera : [];
    return lista.filter((conversa) => deveAparecerNaFilaRealCaptaZap(conversa));
  }, [filaDeEspera]);

  const pendenciasAgendaVisiveis = useMemo(() => {
    return Array.isArray(pendenciaAgendaConversas)
      ? pendenciaAgendaConversas.filter(Boolean)
      : [];
  }, [pendenciaAgendaConversas]);

  useEffect(() => {
    if (typeof onBuscarOperacaoGlobal !== "function") return undefined;

    const termo = String(buscaDiferida || "").trim();
    if (termo.length < 3) {
      onBuscarOperacaoGlobal("");
      return undefined;
    }

    const timer = window.setTimeout(() => {
      onBuscarOperacaoGlobal(termo, { aba });
    }, 280);

    return () => window.clearTimeout(timer);
  }, [aba, buscaDiferida, onBuscarOperacaoGlobal]);

  useEffect(() => {
    if (typeof onBuscarContatosCRMGlobal !== "function") return undefined;

    const termo = String(buscaDiferida || "").trim();
    if (aba !== "contatos" || termo.length < 3) {
      onBuscarContatosCRMGlobal("");
      return undefined;
    }

    const timer = window.setTimeout(() => {
      onBuscarContatosCRMGlobal(termo);
    }, 220);

    return () => window.clearTimeout(timer);
  }, [aba, buscaDiferida, onBuscarContatosCRMGlobal]);

  const conversasPorAba = {
    minhas: minhasConversasVisiveis,
    fila: filaRealVisivel,
    agenda_pendencia: pendenciasAgendaVisiveis,
    followup: followUpsPendentesVisiveis,
    contatos: contatosEfetivos,
    fechados: fechadosEfetivos,
  };

  const conversasBase = conversasPorAba[aba] || [];

  const conversas = useMemo(() => {
    const termo = normalizar(buscaDiferida);
    const resultadosOperacao = Array.isArray(buscaOperacionalGlobal)
      ? buscaOperacionalGlobal
      : [];

    const base =
      aba === "contatos"
        ? termo
          ? combinarListasBuscaContatosCaptaZap(
              // Com busca ativa, pesquisar na base CRM completa, não só nos 220 visíveis.
              contatosBuscaCRMCompleta,
              contatosEfetivos,
              resultadosOperacao,
              conversaSelecionada ? [conversaSelecionada] : [],
            )
          : contatosEfetivos
        : termo
          ? combinarListasBuscaContatosCaptaZap(
              conversasBase,
              resultadosOperacao,
              conversaSelecionada ? [conversaSelecionada] : [],
            )
          : conversasBase;

    const listaBase = dedupeConversasLocal(base);
    const listaFiltradaPorBusca = termo
      ? listaBase.filter((conversa) => conversaBateBuscaGlobal(conversa, termo))
      : listaBase;
    const aplicarFiltroNaoLidas =
      somenteNaoLidas && aba !== "contatos" && aba !== "fechados";
    const listaFiltrada = aplicarFiltroNaoLidas
      ? listaFiltradaPorBusca.filter((conversa) =>
          conversaAguardaRespostaCaptadorCard(conversa),
        )
      : listaFiltradaPorBusca;

    const ordenadas = ordenarConversasPorChegada(listaFiltrada, aba);

    // Em Minhas, TODO acompanhamento fixado é prioridade máxima:
    // sobe para o topo independentemente de quem está atendendo o chamado.
    // Quando houver mais de um fixado, o fixado mais recentemente assume
    // a primeira posição. Assim o novo alerta fica imediatamente visível.
    if (aba !== "minhas") return ordenadas;

    return ordenadas
      .map((conversa, indice) => ({ conversa, indice }))
      .sort((a, b) => {
        const aFixacao = getFixacaoConversaQueueList(a.conversa, fixacoesPorChave);
        const bFixacao = getFixacaoConversaQueueList(b.conversa, fixacoesPorChave);
        const aFixada = Boolean(aFixacao);
        const bFixada = Boolean(bFixacao);

        if (aFixada !== bFixada) return bFixada ? 1 : -1;

        if (aFixada && bFixada) {
          const dataAFixada =
            Date.parse(
              aFixacao?.fixado_em ||
                aFixacao?.fixadoEm ||
                aFixacao?.atualizado_em ||
                aFixacao?.updated_at ||
                "",
            ) || 0;
          const dataBFixada =
            Date.parse(
              bFixacao?.fixado_em ||
                bFixacao?.fixadoEm ||
                bFixacao?.atualizado_em ||
                bFixacao?.updated_at ||
                "",
            ) || 0;

          if (dataAFixada !== dataBFixada) return dataBFixada - dataAFixada;
        }

        return a.indice - b.indice;
      })
      .map((item) => item.conversa);
  }, [
    aba,
    agora,
    buscaDiferida,
    somenteNaoLidas,
    conversasBase,
    contatosBuscaGlobal,
    contatosBuscaCRMCompleta,
    buscaOperacionalGlobal,
    contatosEfetivos,
    minhasConversasVisiveis,
    filaRealVisivel,
    pendenciasAgendaVisiveis,
    followUpsPendentesVisiveis,
    fechadosEfetivos,
    conversaSelecionada,
    conversasFixadasSet,
    fixacoesPorChave,
    usuarioAtualId,
    usuarioAtualNome,
  ]);

  const buscaGlobalAtiva =
    Boolean(normalizar(buscaLocal)) &&
    ((aba === "contatos" &&
      Array.isArray(contatosBuscaGlobal) &&
      contatosBuscaGlobal.length > contatosEfetivos.length) ||
      (Array.isArray(buscaOperacionalGlobal) &&
        buscaOperacionalGlobal.length > 0));

  const handleChangeAba = (novaAba) => {
    setAba(novaAba);
    onChangeAba?.(novaAba);
  };

  const permiteFiltroNaoLidas = aba !== "contatos" && aba !== "fechados";

  const paginacaoAtual = paginacaoPorAba?.[aba] || null;
  const abaPaginada = ["minhas", "fila", "fechados"].includes(aba);

  const handleScrollLista = (event) => {
    if (!abaPaginada || typeof onCarregarMais !== "function") return;
    if (normalizar(buscaLocal)) return;
    if (!paginacaoAtual?.hasMore || paginacaoAtual?.loading) return;

    const target = event?.currentTarget;
    if (!target) return;

    const distanciaDoFim =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanciaDoFim <= 260) {
      onCarregarMais(aba);
    }
  };

  const abaAtual =
    TAB_CONFIG.find((tab) => tab.key === aba) ||
    (aba === "agenda_pendencia"
      ? {
          empty:
            "Nenhuma pendência de agenda. Amanhã está confirmado ou as faltas/cancelamentos já foram reagendados.",
        }
      : aba === "followup"
        ? {
            empty:
              "Nenhum follow-up pendente. Se existir paciente parado, confira se a última mensagem foi enviada pelo operador e se o prazo da regra já venceu.",
          }
        : null);

  return (
    <section className="flex h-full w-full flex-col bg-white">
      <header className="relative shrink-0 overflow-hidden border-b border-amber-300/90 bg-gradient-to-br from-amber-100 via-orange-100 to-amber-50 px-3 py-3 shadow-[0_16px_40px_rgba(120,53,15,0.16)]">
        <div className="pointer-events-none absolute -right-10 -top-14 h-32 w-32 rounded-full bg-amber-400/35 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-12 h-36 w-36 rounded-full bg-orange-300/45 blur-3xl" />
        <div className="relative">
          <div className="mb-3 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/90 bg-white/70 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-amber-700 shadow-sm">
                <Headphones size={12} />
                Conversas
              </div>
              <p className="mt-1.5 truncate text-[11px] font-semibold text-amber-950/65">
                Atendimento, fila, follow-up, contatos e histórico da empresa
                ativa.
              </p>
            </div>

            <button
              type="button"
              title="Filtros"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-amber-300 bg-white/80 text-amber-700 shadow-sm transition hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-100 hover:text-amber-900"
            >
              <SlidersHorizontal size={15} />
            </button>
          </div>

          <div className="rounded-[22px] border border-amber-400 bg-amber-100/75 p-1.5 shadow-inner shadow-amber-900/10 backdrop-blur">
            <nav className="grid grid-cols-3 gap-1.5">
              {TAB_CONFIG.map((tab) => {
                const active = aba === tab.key;
                const Icon = tab.icon;
                const totalRemoto = totaisPorAba?.[tab.key];
                const count =
                  totalRemoto !== null &&
                  totalRemoto !== undefined &&
                  Number.isFinite(Number(totalRemoto))
                    ? Number(totalRemoto)
                    : getTabCount(tab.key, {
                        minhasConversas: minhasConversasVisiveis,
                        filaDeEspera: filaRealVisivel,
                        followUpConversas: followUpsPendentesVisiveis,
                        contatos: contatosEfetivos,
                        fechadosConversas: fechadosEfetivos,
                        totalFechados,
                      });

                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => handleChangeAba(tab.key)}
                    className={`relative flex h-10 items-center justify-center gap-1 rounded-2xl px-1 text-[11px] font-black transition ${
                      active
                        ? `border border-white/55 bg-gradient-to-br ${tab.activeClass} shadow-lg ring-1 ring-black/10`
                        : "border border-amber-300 bg-white/90 text-slate-700 shadow-sm ring-1 ring-amber-200/80 hover:-translate-y-0.5 hover:border-amber-500 hover:bg-amber-100 hover:text-amber-950 hover:shadow-md"
                    }`}
                    title={tab.label}
                  >
                    <Icon size={13} className="shrink-0" />
                    <span className="truncate leading-none">{tab.label}</span>
                    <span
                      className={`flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-black ${
                        active ? "bg-white/20 text-white" : tab.badgeClass
                      }`}
                    >
                      {count > 99 ? "99+" : count}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="mt-2 grid grid-cols-[1.3fr_1fr_1fr] gap-1.5">
            <button
              type="button"
              onClick={() => handleChangeAba("agenda_pendencia")}
              title="Pendência Agenda · confirmar amanhã e reagendar faltas"
              className={`flex h-10 min-w-0 items-center justify-between gap-1 rounded-xl border px-1.5 text-left shadow-sm transition ${
                aba === "agenda_pendencia"
                  ? "border-emerald-600 bg-gradient-to-br from-emerald-500 via-green-600 to-emerald-800 text-white ring-1 ring-emerald-100 shadow-md shadow-emerald-900/15"
                  : "border-emerald-300 bg-white/95 text-slate-700 hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-900"
              }`}
            >
              <span className="flex min-w-0 items-center gap-1">
                <span
                  className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-lg ${
                    aba === "agenda_pendencia"
                      ? "bg-white/15 text-white"
                      : "bg-emerald-100 text-emerald-700"
                  }`}
                >
                  <CalendarCheck size={13} />
                </span>
                <span className="min-w-0 whitespace-normal text-[10px] font-black leading-[11px]">
                  Pendência Agenda
                </span>
              </span>
              <span
                className={`flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-black ${
                  aba === "agenda_pendencia"
                    ? "bg-white/20 text-white"
                    : "bg-emerald-100 text-emerald-700"
                }`}
              >
                {pendenciasAgendaVisiveis.length > 99
                  ? "99+"
                  : pendenciasAgendaVisiveis.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleChangeAba("followup")}
              title="Follow-up pendentes"
              className={`flex h-10 min-w-0 items-center justify-between gap-1 rounded-xl border px-1.5 text-left shadow-sm transition ${
                aba === "followup"
                  ? "border-rose-500 bg-gradient-to-br from-rose-500 via-orange-500 to-amber-500 text-white ring-1 ring-rose-100 shadow-md shadow-orange-900/15"
                  : "border-orange-300 bg-white/95 text-slate-700 hover:border-orange-500 hover:bg-orange-50 hover:text-orange-900"
              }`}
            >
              <span className="flex min-w-0 items-center gap-1">
                <span
                  className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-lg ${
                    aba === "followup"
                      ? "bg-white/15 text-white"
                      : "bg-orange-100 text-orange-700"
                  }`}
                >
                  <RotateCcw size={13} />
                </span>
                <span className="min-w-0 whitespace-nowrap text-[10px] font-black leading-tight">
                  Follow-up
                </span>
              </span>
              <span
                className={`flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-black ${
                  aba === "followup"
                    ? "bg-white/20 text-white"
                    : "bg-orange-100 text-orange-700"
                }`}
              >
                {followUpsPendentesVisiveis.length > 99
                  ? "99+"
                  : followUpsPendentesVisiveis.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleChangeAba("fechados")}
              title="Histórico de chamados fechados"
              className={`flex h-10 min-w-0 items-center justify-between gap-1 rounded-xl border px-1.5 text-left shadow-sm transition ${
                aba === "fechados"
                  ? "border-slate-600 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-950 text-white ring-1 ring-slate-200 shadow-md shadow-slate-900/15"
                  : "border-slate-300 bg-white/95 text-slate-700 hover:border-slate-500 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              <span className="flex min-w-0 items-center gap-1">
                <span
                  className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-lg ${
                    aba === "fechados"
                      ? "bg-white/15 text-white"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  <Archive size={13} />
                </span>
                <span className="min-w-0 whitespace-nowrap text-[10px] font-black leading-tight">
                  Histórico
                </span>
              </span>
              <span
                className={`flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-black ${
                  aba === "fechados"
                    ? "bg-white/20 text-white"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {(
                  totaisPorAba?.fechados ??
                  totalFechados ??
                  fechadosEfetivos.length
                ) > 99
                  ? "99+"
                  : totalsafeQueueList(
                      totaisPorAba?.fechados,
                      totalFechados,
                      fechadosEfetivos.length,
                    )}
              </span>
            </button>
          </div>

          <div className="mt-2 flex items-stretch gap-1.5">
            <div className="relative min-w-0 flex-1 rounded-xl border border-amber-400 bg-amber-100/80 p-1 shadow-sm ring-1 ring-amber-200/70 backdrop-blur">
              <Search
                size={14}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-amber-600"
              />
              <input
                value={buscaLocal}
                onChange={(event) => setBuscaLocal(event.target.value)}
                placeholder={
                  aba === "contatos"
                    ? "Buscar em todas as leads por nome ou telefone"
                    : aba === "agenda_pendencia"
                      ? "Buscar pendências de agenda"
                      : aba === "followup"
                        ? "Buscar follow-ups pendentes"
                        : aba === "fechados"
                          ? "Buscar chamados fechados"
                          : "Buscar nesta lista ou na operação"
                }
                className="h-9 w-full rounded-lg border border-amber-300 bg-white/95 pl-8 pr-2.5 text-[11px] font-semibold text-slate-800 shadow-sm outline-none transition placeholder:text-amber-900/45 focus:border-amber-600 focus:ring-2 focus:ring-amber-500/20"
              />
            </div>

            {permiteFiltroNaoLidas ? (
              <button
                type="button"
                aria-pressed={somenteNaoLidas}
                title={
                  somenteNaoLidas
                    ? "Mostrar todas as conversas"
                    : "Mostrar somente pacientes aguardando resposta do captador"
                }
                onClick={() => setSomenteNaoLidas((ativo) => !ativo)}
                className={`flex min-w-[78px] shrink-0 items-center justify-center gap-1 rounded-xl border px-2 text-[10px] font-black shadow-sm transition ${
                  somenteNaoLidas
                    ? "border-blue-700 bg-blue-700 text-white ring-1 ring-blue-200"
                    : "border-amber-400 bg-white/95 text-amber-800 ring-1 ring-amber-200 hover:border-amber-600 hover:bg-amber-50"
                }`}
              >
                <BellRing size={13} />
                Não lidas
              </button>
            ) : null}
          </div>

          {aba === "contatos" && (
            <div className="mt-2 flex items-center justify-between gap-2 text-[11px] font-semibold text-amber-950/65">
              <span>
                {buscaGlobalAtiva
                  ? `${buscandoContatosCRMGlobal ? "Buscando no CRM... " : buscandoOperacaoGlobal ? "Buscando na operação... " : ""}Busca geral: ${conversas.length} resultado(s)`
                  : `Mostrando ${contatosEfetivos.length} de ${
                      totalContatosVisiveis || contatosEfetivos.length
                    } contatos`}
              </span>
              {totalContatosCRM > 0 && (
                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-amber-800 ring-1 ring-amber-300">
                  Base CRM: {totalContatosCRM}
                </span>
              )}
            </div>
          )}

          {aba !== "contatos" && normalizar(buscaLocal) ? (
            <div className="mt-2 flex items-center justify-between gap-2 text-[11px] font-semibold text-amber-950/65">
              <span>
                {buscandoOperacaoGlobal
                  ? "Buscando também na operação..."
                  : `Busca nesta lista/operação: ${conversas.length} resultado(s)`}
              </span>
              {Array.isArray(buscaOperacionalGlobal) &&
              buscaOperacionalGlobal.length > 0 ? (
                <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-blue-800 ring-1 ring-blue-300">
                  Geral: {buscaOperacionalGlobal.length}
                </span>
              ) : null}
            </div>
          ) : null}

          {aba === "fechados" && (
            <div className="mt-2 flex items-center justify-between gap-2 text-[11px] font-semibold text-amber-950/65">
              <span>Histórico de chamados fechados: {conversas.length}</span>
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-amber-800 ring-1 ring-amber-300">
                Total: {totalFechados || fechadosEfetivos.length}
              </span>
            </div>
          )}
        </div>
      </header>

      <div
        className="min-h-0 flex-1 overflow-y-auto bg-[#F3F6FB] px-2 py-2"
        onScroll={handleScrollLista}
      >
        {aba === "agenda_pendencia" && pendenciasAgendaVisiveis.length > 0 ? (
          <div className="mb-2 rounded-[20px] border border-emerald-200 bg-white px-3 py-2 shadow-sm">
            <div className="flex items-center gap-2 text-[12px] font-black text-emerald-700">
              <CalendarCheck size={14} />
              Pendência Agenda
            </div>
            <p className="mt-0.5 text-[11px] font-medium leading-4 text-slate-500">
              Confirme os agendamentos de amanhã e reagende pacientes que
              faltaram ou cancelaram. Ao confirmar/reagendar, eles saem daqui
              automaticamente.
            </p>
          </div>
        ) : null}

        {aba === "followup" && followUpResumoTexto ? (
          <div className="mb-2 rounded-[20px] border border-rose-200 bg-white px-3 py-2 shadow-sm">
            <div className="flex items-center gap-2 text-[12px] font-black text-rose-700">
              <RotateCcw size={14} />
              Follow-up pendente
            </div>
            <p className="mt-0.5 text-[11px] font-medium leading-4 text-slate-500">
              {followUpResumoTexto}
            </p>
          </div>
        ) : null}

        {aba === "fechados" && conversas.length > 0 && (
          <div className="mb-2 rounded-[20px] border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <div className="flex items-center gap-2 text-[12px] font-black text-slate-700">
              <Archive size={14} />
              Chamados fechados
            </div>
            <p className="mt-0.5 text-[11px] font-medium leading-4 text-slate-500">
              Histórico de atendimentos encerrados. Eles não aparecem em Minhas
              nem na Fila operacional.
            </p>
          </div>
        )}

        {conversas.length === 0 && paginacaoAtual?.loading ? (
          <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-5 text-[11px] font-bold text-slate-500 shadow-sm">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            Carregando conversas...
          </div>
        ) : conversas.length === 0 ? (
          <EmptyList
            message={
              somenteNaoLidas && permiteFiltroNaoLidas
                ? "Nenhum paciente aguardando resposta do captador nesta lista."
                : abaAtual?.empty || "Nenhuma conversa encontrada."
            }
          />
        ) : (
          <div className="space-y-1.5">
            {conversas.map((conversa) => {
              const selected = isSameConversation(
                conversaSelecionada,
                conversa,
              );
              return (
                <ConversationItem
                  key={getConversationKey(conversa)}
                  conversa={conversa}
                  selected={selected}
                  usuarios={usuarios}
                  agora={agora}
                  aba={aba}
                  followUpRegras={followUpRegras}
                  conexoesWhatsApp={conexoesWhatsApp}
                  mostrarConexao={
                    Array.isArray(conexoesWhatsApp) &&
                    conexoesWhatsApp.length > 1
                  }
                  fixado={conversasFixadasSet.has(
                    getChaveConversaFixadaQueueList(conversa),
                  )}
                  fixacao={getFixacaoConversaQueueList(
                    conversa,
                    fixacoesPorChave,
                  )}
                  onClick={() => onSelectConversa?.(conversa)}
                />
              );
            })}

            {abaPaginada && paginacaoAtual?.loading ? (
              <div className="flex items-center justify-center gap-2 py-3 text-[10px] font-bold text-slate-500">
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
                Carregando mais...
              </div>
            ) : null}

            {abaPaginada &&
            paginacaoAtual?.carregada &&
            !paginacaoAtual?.hasMore &&
            !normalizar(buscaLocal) ? (
              <div className="py-2 text-center text-[9px] font-bold uppercase tracking-wide text-slate-400">
                Fim da lista
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}


function totalsafeQueueList(...valores) {
  for (const valor of valores) {
    if (valor === null || valor === undefined || valor === "") continue;
    const numero = Number(valor);
    if (Number.isFinite(numero)) return numero;
  }
  return 0;
}


function getNomeWhatsappConversaCard(conversa = {}) {
  return String(
    conversa?.nome_whatsapp ||
      conversa?.nomeWhatsapp ||
      conversa?.contato_nome_whatsapp ||
      conversa?.contatoNomeWhatsapp ||
      "",
  )
    .replace(/\s+/g, " ")
    .trim();
}

function nomesVisuaisIguaisCard(a, b) {
  const normalizarNome = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const na = normalizarNome(a);
  const nb = normalizarNome(b);

  return Boolean(na && nb && na === nb);
}

function ConversationItem({
  conversa,
  selected,
  usuarios = [],
  agora,
  aba,
  followUpRegras = [],
  conexoesWhatsApp = [],
  mostrarConexao = false,
  fixado = false,
  fixacao = null,
  onClick,
}) {
  const leadsVinculadasCard = getLeadsVinculadasCard(conversa);
  const leadContextoCard = getLeadPrincipalConversaCard(conversa);
  const nomeLeadOficial = getNomeLeadVinculadaCard(leadContextoCard) || "";
  const nomeWhatsapp = getNomeWhatsappConversaCard(conversa);
  const nome =
    nomeLeadOficial ||
    conversa.nome ||
    conversa.nome_lead ||
    conversa.nomeLead ||
    conversa.nome_cliente ||
    conversa.paciente ||
    nomeWhatsapp ||
    conversa.numero ||
    conversa.telefone ||
    conversa.telefone1 ||
    "Contato sem nome";
  const nomePrincipalEhWhatsapp =
    Boolean(nomeWhatsapp) && nomesVisuaisIguaisCard(nome, nomeWhatsapp);
  const mostrarNomeWhatsappSecundario =
    Boolean(nomeWhatsapp) && !nomesVisuaisIguaisCard(nome, nomeWhatsapp);
  const hora = conversa.hora || conversa.ultimaMensagemEm || "";
  const canal = conversa.canal || "whatsapp";
  const nomeConexao =
    mostrarConexao && isCanalWhatsappCard(canal)
      ? getNomeConexaoWhatsappCard(conversa, conexoesWhatsApp)
      : "";
  const naoLidas = Number(conversa.naoLidas || 0);
  const canalStyle = getCanalStyle(canal);
  const entradaCaptaZap = formatarEntradaCaptaZapCard(conversa, {
    curto: true,
  });
  const entradaCaptaZapCompleta = entradaCaptaZap;

  const leadResumo = getLeadResumo(conversa, usuarios);
  const reaberturaResponder = isReaberturaResponderCard(conversa);
  const chamadoFechado =
    aba === "fechados" ||
    (isChamadoFechadoCard(conversa) && !reaberturaResponder);
  const motivoFechamento = getMotivoFechamentoCard(conversa);
  const fechadoPorFollowup =
    chamadoFechado && isFechadoPorFollowupCard(conversa);
  const resumoFechamentoFollowup = fechadoPorFollowup
    ? getResumoFechamentoFollowupCard(conversa)
    : null;
  const statusStyle = chamadoFechado
    ? getStatusStyle("fechado")
    : getStatusStyle(leadResumo.status);
  const StatusIcon = statusStyle.icon;
  const statusEhReativavel = normalizar(leadResumo.status).includes("reativ");
  const valorOportunidade = formatMoney(leadResumo.valor);
  const captadorLead = leadResumo.captadorLead;
  const responsavelChamado = leadResumo.responsavelChamado;
  const fonteLead = leadResumo.fonte;
  const draMaluEmRecuperacao = isDraMaluEmRecuperacaoCard(conversa);
  const recuperadoDraMalu = isRecuperadoDraMaluCard(conversa);
  const filaProtecao = getFilaProtecaoCard(conversa);
  const filaProtecaoVisual = getFilaProtecaoVisualCard(filaProtecao, {
    recuperadoDraMalu,
  });
  const recuperadoDraMaluEmFila = Boolean(
    filaProtecao.ativa && filaProtecaoVisual.tipo === "dra_malu",
  );
  const responsavelAgoraDraMalu =
    nomeCurtoCard(
      responsavelChamado ||
        captadorLead ||
        conversa.responsavel_nome ||
        conversa.captador_nome ||
        conversa.usuario_responsavel_nome ||
        "",
    ) || "captador";
  const mensagemPreviewOriginal = getMensagemPreviewCard(conversa, leadResumo);
  const mensagemPreview = draMaluEmRecuperacao
    ? DRA_MALU_EM_RECUPERACAO_BADGE
    : recuperadoDraMalu
      ? recuperadoDraMaluEmFila
        ? "🟣 Recuperado pela Dra. Malu · Na Fila para continuidade"
        : `🟡 Recuperado pela Dra. Malu · Agora com ${responsavelAgoraDraMalu}`
      : mensagemPreviewOriginal;
  const estadoAtendimento = getEstadoAtendimentoVisualCard(conversa, {
    chamadoFechado,
    reaberturaResponder,
    agora,
  });
  const esperaResposta = estadoAtendimento.esperaResposta;
  const precisaResponder = estadoAtendimento.estado === "responder";
  const respondido = estadoAtendimento.estado === "respondido";
  const followUpPendente = chamadoFechado
    ? null
    : getTempoFollowUpCard(conversa, agora);
  const destaquePaciente = getDestaquePacienteCard(conversa, leadResumo);
  const somenteContato = isSomenteContatoCaptaZapCard(conversa);
  const acompanhamentoPorNome = String(
    fixacao?.usuario_nome || fixacao?.usuarioNome || "",
  ).trim();
  const linhaResumoCard = montarLinhaResumoOperacionalCard({
    responsavelChamado,
    captadorLead,
    fonteLead: getFonteOuCampanhaCard(conversa, leadResumo),
  });
  const proximaAcaoFollowUpCard =
    chamadoFechado || precisaResponder
      ? null
      : getProximaAcaoFollowUpSutilCard(conversa, agora, followUpRegras);

  if (aba === "followup") {
    return (
      <FollowUpConversationItem
        conversa={conversa}
        selected={selected}
        canalStyle={canalStyle}
        canal={canal}
        nome={nome}
        nomeWhatsapp={nomeWhatsapp}
        nomePrincipalEhWhatsapp={nomePrincipalEhWhatsapp}
        mostrarNomeWhatsappSecundario={mostrarNomeWhatsappSecundario}
        mensagemPreview={mensagemPreview}
        leadResumo={leadResumo}
        linhaResumoCard={linhaResumoCard}
        nomeConexao={nomeConexao}
        mostrarConexao={mostrarConexao}
        agora={agora}
        onClick={onClick}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex w-full items-start gap-2 overflow-hidden rounded-[16px] border px-2 py-1.5 text-left transition ${
        selected
          ? filaProtecao.ativa
            ? filaProtecaoVisual.cardSelectedClass
            : recuperadoDraMalu
              ? "border-amber-500 bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 shadow-[0_12px_28px_rgba(245,158,11,0.22)] ring-2 ring-amber-300"
              : somenteContato
                ? "border-orange-500 bg-gradient-to-br from-orange-50 via-white to-amber-50 shadow-[0_10px_24px_rgba(249,115,22,0.18)] ring-2 ring-orange-200"
                : "border-blue-500 bg-white shadow-[0_10px_24px_rgba(37,99,235,0.16)] ring-2 ring-blue-100"
          : filaProtecao.ativa
            ? filaProtecaoVisual.cardClass
            : recuperadoDraMalu
              ? "border-amber-500 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-100 shadow-[0_10px_26px_rgba(245,158,11,0.20)] ring-2 ring-amber-200 hover:-translate-y-0.5 hover:border-amber-600 hover:ring-amber-300 hover:shadow-[0_14px_30px_rgba(245,158,11,0.24)]"
              : somenteContato
                ? "border-orange-300 bg-gradient-to-br from-orange-50 via-white to-amber-50 shadow-[0_6px_18px_rgba(249,115,22,0.12)] ring-1 ring-orange-100 hover:-translate-y-0.5 hover:border-orange-400 hover:ring-orange-200 hover:shadow-[0_10px_24px_rgba(249,115,22,0.18)]"
                : fechadoPorFollowup
                  ? "border-amber-500 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-[0_8px_24px_rgba(245,158,11,0.16)] ring-1 ring-amber-200 hover:-translate-y-0.5 hover:border-amber-600 hover:ring-amber-300 hover:shadow-[0_12px_28px_rgba(245,158,11,0.20)]"
                  : chamadoFechado
                    ? "border-slate-500 bg-slate-100 shadow-[0_4px_14px_rgba(15,23,42,0.08)] ring-1 ring-slate-300 hover:-translate-y-0.5 hover:border-slate-700 hover:bg-white hover:shadow-[0_12px_24px_rgba(15,23,42,0.12)]"
                    : precisaResponder
                      ? "border-emerald-300 bg-white shadow-[0_8px_22px_rgba(16,185,129,0.13)] ring-1 ring-emerald-100 hover:-translate-y-0.5 hover:border-emerald-400 hover:ring-emerald-200 hover:shadow-[0_12px_26px_rgba(16,185,129,0.18)]"
                      : respondido
                        ? "border-blue-300 bg-white shadow-[0_6px_18px_rgba(37,99,235,0.10)] ring-1 ring-blue-100 hover:-translate-y-0.5 hover:border-blue-400 hover:ring-blue-200 hover:shadow-[0_10px_22px_rgba(37,99,235,0.14)]"
                        : destaquePaciente.nivel === "paciente"
                          ? "border-yellow-500 bg-gradient-to-br from-yellow-50 via-white to-amber-100 shadow-[0_8px_24px_rgba(217,119,6,0.18)] ring-1 ring-yellow-300 hover:-translate-y-0.5 hover:border-yellow-600 hover:ring-yellow-400 hover:shadow-[0_12px_26px_rgba(217,119,6,0.24)]"
                          : destaquePaciente.nivel === "lead"
                            ? "border-cyan-300 bg-gradient-to-br from-cyan-50 via-white to-blue-50 shadow-[0_6px_18px_rgba(14,165,233,0.10)] ring-1 ring-cyan-100 hover:-translate-y-0.5 hover:border-cyan-400 hover:ring-cyan-200 hover:shadow-[0_10px_22px_rgba(14,165,233,0.14)]"
                            : "border-slate-300 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.06)] ring-1 ring-slate-100 hover:-translate-y-0.5 hover:border-blue-300 hover:ring-blue-100 hover:shadow-[0_10px_22px_rgba(15,23,42,0.09)]"
      }`}
    >
      <div
        className={`absolute bottom-0 left-0 top-0 w-1 ${
          filaProtecao.ativa
            ? filaProtecaoVisual.accentClass
            : recuperadoDraMalu
              ? "bg-amber-500"
              : somenteContato
                ? "bg-orange-500"
                : fechadoPorFollowup
                  ? "bg-amber-600"
                  : chamadoFechado
                    ? "bg-slate-800"
                    : precisaResponder
                      ? "bg-emerald-500"
                      : respondido
                        ? "bg-blue-500"
                        : destaquePaciente.nivel === "paciente"
                          ? "bg-amber-500"
                          : destaquePaciente.nivel === "lead"
                            ? "bg-cyan-500"
                            : selected
                              ? statusStyle.accent
                              : statusStyle.accentSoft
        }`}
      />

      {destaquePaciente.nivel === "paciente" ? (
        <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-1.5 bg-gradient-to-b from-yellow-400 via-amber-500 to-yellow-600" />
      ) : null}

      {filaProtecao.ativa ? (
        <div
          className={`absolute right-2 top-0 z-20 flex -translate-y-px items-center gap-1 rounded-b-lg px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide shadow-sm ring-1 ${filaProtecaoVisual.topBadgeClass}`}
          title={`${filaProtecaoVisual.label || "Fila de proteção"}${
            responsavelChamado ? ` · Responsável preservado: ${responsavelChamado}` : ""
          }`}
        >
          {filaProtecaoVisual.tipo === "dra_malu" ? (
            <Sparkles size={10} />
          ) : (
            <ShieldAlert size={10} />
          )}
          {filaProtecaoVisual.topLabel}
        </div>
      ) : recuperadoDraMalu ? (
        <div
          className="absolute right-2 top-0 z-20 flex -translate-y-px items-center gap-1 rounded-b-lg bg-amber-500 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-amber-950 shadow-sm shadow-amber-900/20 ring-1 ring-amber-300"
          title={DRA_MALU_RECUPERADO_TITULO}
        >
          <Sparkles size={10} />
          Agora com {responsavelAgoraDraMalu}
        </div>
      ) : fechadoPorFollowup ? (
        <div
          className="absolute right-2 top-0 z-10 flex -translate-y-px items-center gap-1 rounded-b-lg bg-amber-700 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-white shadow-sm shadow-amber-900/25"
          title={
            resumoFechamentoFollowup?.title || "Chamado fechado por follow-up"
          }
        >
          <RotateCcw size={10} />
          Follow-up fechado
        </div>
      ) : chamadoFechado ? (
        <div className="absolute right-2 top-0 z-10 flex -translate-y-px items-center gap-1 rounded-b-lg bg-slate-800 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-white shadow-sm shadow-slate-950/25">
          <Archive size={10} />
          Fechado
        </div>
      ) : precisaResponder ? (
        <div className="absolute right-2 top-0 z-10 flex -translate-y-px items-center gap-1 rounded-b-lg bg-emerald-500 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-white shadow-sm shadow-emerald-900/20">
          <BellRing size={10} />
          Responder
        </div>
      ) : respondido ? (
        <div className="absolute right-2 top-0 z-10 flex -translate-y-px items-center gap-1 rounded-b-lg bg-blue-500 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-white shadow-sm shadow-blue-900/20">
          <CheckCheck size={10} />
          Respondido
        </div>
      ) : null}

      <div className="relative ml-0.5 shrink-0">
        {conversa.foto ? (
          <img
            src={conversa.foto}
            alt={nome}
            className={`h-9 w-9 rounded-full object-cover ring-2 ${
              selected ? canalStyle.avatarRing : "ring-slate-200"
            }`}
          />
        ) : (
          <div
            className={`flex h-9 w-9 items-center justify-center rounded-full ring-2 ${
              selected
                ? canalStyle.avatarBg
                : "bg-slate-100 text-slate-400 ring-slate-200"
            }`}
          >
            <FaUserCircle size={22} />
          </div>
        )}

        <span
          className={`absolute -bottom-0.5 -right-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-white shadow-sm ${canalStyle.iconBg}`}
        >
          <CanalIcon canal={canal} />
        </span>

        {conversa.favorito && (
          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 text-white shadow-sm ring-2 ring-white">
            <FaStar size={8} />
          </span>
        )}

      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <div
                className={`min-w-0 truncate text-[12px] font-bold leading-tight ${
                  recuperadoDraMalu
                    ? "text-amber-950"
                    : selected
                      ? "text-blue-950"
                      : "text-slate-900"
                }`}
                title={nome}
              >
                {nome}
              </div>

              {nomePrincipalEhWhatsapp ? (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-emerald-700"
                  title="Nome de exibição informado pelo perfil do WhatsApp"
                >
                  <FaWhatsapp size={8} />
                  WhatsApp
                </span>
              ) : null}

              {mostrarConexao && nomeConexao ? (
                <span
                  className="inline-flex max-w-[82px] shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-indigo-700"
                  title={`Conexão: ${nomeConexao}`}
                >
                  <span className="truncate">{nomeConexao}</span>
                </span>
              ) : null}
            </div>

            {mostrarNomeWhatsappSecundario ? (
              <div
                className="mt-px flex min-w-0 items-center gap-1 text-[9.5px] font-bold text-emerald-700"
                title={`Nome do WhatsApp: ${nomeWhatsapp}`}
              >
                <FaWhatsapp size={9} className="shrink-0" />
                <span className="min-w-0 truncate">
                  WhatsApp: {nomeWhatsapp}
                </span>
              </div>
            ) : null}

            <div className="mt-px flex min-w-0 items-center gap-1 text-[10px] font-semibold text-slate-500">
              <span
                className="min-w-0 whitespace-nowrap leading-tight"
                title={formatarTelefoneCard(
                  conversa.numero ||
                    conversa.telefone ||
                    conversa.telefone1 ||
                    canal,
                )}
              >
                {formatarTelefoneCard(
                  conversa.numero ||
                    conversa.telefone ||
                    conversa.telefone1 ||
                    canal,
                )}
              </span>
              {/* Regra operacional: o card da fila não exibe mais o selo "2 pacientes".
                  A lista lateral deve mostrar apenas o atendimento/conversa atual.
                  Se houver telefone compartilhado real, o contexto fica no painel/barra de detalhes,
                  evitando ruído visual e falso alerta no QueueList. */}
            </div>
            {/* Contexto do paciente removido do card lateral.
                O nome do card já representa o paciente/conversa atual; repetir "Contexto"
                confundia a operação e poluía a fila. */}
          </div>

          <div className="flex w-[92px] shrink-0 flex-col items-end gap-0.5 pt-3">
            <div className="flex w-full items-center justify-end gap-1.5">
              {fixado ? (
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-red-500 bg-gradient-to-br from-red-600 via-orange-500 to-amber-400 text-white shadow-lg shadow-red-500/45 ring-2 ring-white"
                  title={
                    acompanhamentoPorNome
                      ? `Acompanhada por: ${acompanhamentoPorNome}`
                      : "Conversa em acompanhamento"
                  }
                  aria-label={
                    acompanhamentoPorNome
                      ? `Acompanhada por ${acompanhamentoPorNome}`
                      : "Conversa em acompanhamento"
                  }
                >
                  <Flame size={17} fill="currentColor" strokeWidth={2.2} />
                </span>
              ) : null}

              {hora && (
                <span
                  className={`text-[9px] font-bold leading-none ${
                    selected ? "text-blue-500" : "text-slate-400"
                  }`}
                >
                  {hora}
                </span>
              )}
            </div>

            {entradaCaptaZap ? (
              <span
                className="inline-flex w-full items-center justify-end gap-1 rounded-md bg-blue-50 px-1 py-px text-right text-[8.5px] font-bold leading-3 text-blue-700 ring-1 ring-blue-100"
                title={
                  entradaCaptaZapCompleta
                    ? `Entrada no CaptaZap: ${entradaCaptaZapCompleta}`
                    : "Entrada no CaptaZap"
                }
              >
                <CalendarCheck size={9} className="shrink-0 text-blue-500" />
                <span className="truncate whitespace-nowrap leading-3">
                  {entradaCaptaZap}
                </span>
              </span>
            ) : null}

            {naoLidas > 0 && !chamadoFechado && (
              <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-blue-600 px-1.5 text-[10px] font-black leading-none text-white shadow-sm">
                {naoLidas > 99 ? "99+" : naoLidas}
              </span>
            )}

            {chamadoFechado ? null : esperaResposta ? (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-emerald-700 shadow-sm"
                title={`Cliente aguardando resposta há ${esperaResposta.label}`}
              >
                <BellRing size={9} />
                {esperaResposta.shortLabel}
              </span>
            ) : followUpPendente ? (
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold leading-none shadow-sm ${
                  followUpPendente.perdidoSugerido
                    ? "border-slate-300 bg-slate-800 text-white"
                    : "border-rose-200 bg-rose-50 text-rose-700"
                }`}
                title={
                  followUpPendente.descricao ||
                  `Cliente sem retorno há ${followUpPendente.label}`
                }
              >
                <RotateCcw size={9} />
                {followUpPendente.etapaLabel || "Follow-up"}
              </span>
            ) : null}
          </div>
        </div>

        {mensagemPreview ? (
          <div
            className={`mt-0.5 flex min-w-0 items-center gap-1 text-[10px] leading-tight ${
              draMaluEmRecuperacao
                ? "font-black text-violet-800"
                : recuperadoDraMalu
                  ? "font-black text-amber-800"
                  : precisaResponder
                    ? "font-bold text-emerald-700"
                    : respondido
                      ? "font-semibold text-blue-700"
                      : naoLidas > 0 && !chamadoFechado
                        ? "font-bold text-slate-700"
                        : "font-medium text-slate-500"
            }`}
            title={mensagemPreview}
          >
            {draMaluEmRecuperacao ? (
              <img
                src={DRA_MALU_AVATAR_URL}
                alt="Dra. Malu"
                className="h-3.5 w-3.5 shrink-0 rounded-full object-cover ring-1 ring-violet-200"
              />
            ) : null}
            <span className="min-w-0 truncate">{mensagemPreview}</span>
          </div>
        ) : null}

        {chamadoFechado && motivoFechamento ? (
          <div
            className={`mt-0.5 truncate text-[9px] font-bold leading-tight ${
              fechadoPorFollowup ? "text-amber-700" : "text-slate-500"
            }`}
            title={motivoFechamento}
          >
            Motivo: {motivoFechamento}
          </div>
        ) : null}

        {followUpPendente?.descricao && !chamadoFechado ? (
          <div
            className={`mt-0.5 truncate text-[9px] font-bold leading-tight ${
              followUpPendente.perdidoSugerido
                ? "text-slate-700"
                : "text-rose-600"
            }`}
            title={followUpPendente.descricao}
          >
            {followUpPendente.descricao}
          </div>
        ) : null}

        <div className="mt-1 flex min-w-0 items-center gap-1 overflow-hidden">
          {filaProtecao.ativa ? (
            <span
              className={`inline-flex min-w-0 max-w-[178px] items-center gap-1 rounded-full border px-1.5 py-px text-[9px] font-black leading-3 shadow-sm ring-1 ${filaProtecaoVisual.pillClass}`}
              title={`${filaProtecaoVisual.label || "Fila de proteção"}${
                responsavelChamado
                  ? ` — responsável preservado: ${responsavelChamado}`
                  : ""
              }`}
            >
              {filaProtecaoVisual.tipo === "dra_malu" ? (
                <Sparkles size={9} className="shrink-0" />
              ) : (
                <ShieldAlert size={9} className="shrink-0" />
              )}
              <span className="truncate">
                {filaProtecaoVisual.label || "Fila de proteção"}
              </span>
            </span>
          ) : null}

          {somenteContato ? (
            <span
              className="inline-flex min-w-0 max-w-[132px] items-center gap-1 rounded-full border border-orange-300 bg-orange-100 px-1.5 py-px text-[9px] font-black leading-3 text-orange-800 shadow-sm ring-1 ring-orange-200"
              title="Contato salvo no CaptaZap que ainda não foi convertido em lead"
            >
              <UserRound size={9} className="shrink-0" />
              <span className="truncate">Somente contato</span>
            </span>
          ) : (
            <span
              className={`inline-flex min-w-0 max-w-[132px] items-center gap-1 rounded-full border px-1.5 py-px text-[9px] font-semibold leading-3 ${statusStyle.badge}`}
              title={
                chamadoFechado
                  ? "Chamado fechado"
                  : leadResumo.status || "Sem status"
              }
            >
              <StatusIcon size={9} className="shrink-0" />
              <span className="truncate">
                {chamadoFechado
                  ? "Chamado fechado"
                  : leadResumo.status || "Sem status"}
              </span>
            </span>
          )}

          {recuperadoDraMalu && !recuperadoDraMaluEmFila ? (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-1.5 py-px text-[9px] font-black leading-3 text-amber-900 shadow-sm ring-1 ring-amber-200"
              title={DRA_MALU_RECUPERADO_TITULO}
            >
              <Sparkles size={10} />
              Agora com {responsavelAgoraDraMalu}
            </span>
          ) : null}

          {destaquePaciente.nivel ? (
            <span
              className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-px text-[9px] font-semibold leading-3 shadow-sm ring-1 ${
                destaquePaciente.nivel === "paciente"
                  ? "border-yellow-500 bg-gradient-to-r from-yellow-100 via-amber-100 to-yellow-50 text-amber-950 shadow-amber-900/10 ring-yellow-300"
                  : "border-cyan-200 bg-cyan-50 text-cyan-700 shadow-cyan-900/10 ring-cyan-100"
              }`}
              title={destaquePaciente.title}
            >
              <BadgeCheck size={10} />
              {destaquePaciente.chip}
            </span>
          ) : null}

          {valorOportunidade ? (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-px text-[9px] font-black leading-3 text-emerald-700"
              title={`Valor da oportunidade: ${valorOportunidade}`}
            >
              <CircleDollarSign size={10} />
              {valorOportunidade}
            </span>
          ) : null}

          {conversa.transferencia_pendente && !chamadoFechado ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[9px] font-semibold leading-3 text-amber-700">
              <ArrowLeftRight size={10} />
              Transf.
            </span>
          ) : null}

          {(conversa.lead_reativavel || conversa.reativacao_pendente) &&
          !statusEhReativavel &&
          !chamadoFechado ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-1.5 py-px text-[9px] font-semibold leading-3 text-orange-700">
              <RotateCcw size={10} />
              Reativ.
            </span>
          ) : null}
        </div>

        {fechadoPorFollowup && resumoFechamentoFollowup ? (
          <div
            className="mt-1 flex min-w-0 items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold leading-3 text-amber-800 ring-1 ring-amber-200"
            title={resumoFechamentoFollowup.title}
          >
            <Archive size={9} className="shrink-0 opacity-75" />
            <span className="min-w-0 flex-1 truncate">
              {resumoFechamentoFollowup.label}
            </span>
          </div>
        ) : null}

        {linhaResumoCard ? (
          <div
            className="mt-1 flex min-w-0 items-center gap-1 text-[9px] font-medium leading-3 text-slate-500"
            title={linhaResumoCard}
          >
            <UserRound size={9} className="shrink-0 text-slate-400" />
            <span className="truncate">{linhaResumoCard}</span>
          </div>
        ) : null}

        {proximaAcaoFollowUpCard ? (
          <div
            className={`mt-1 flex min-w-0 items-center gap-1 text-[9px] font-semibold leading-3 ${proximaAcaoFollowUpCard.className}`}
            title={proximaAcaoFollowUpCard.title}
          >
            <Clock3 size={9} className="shrink-0 opacity-75" />
            <span className="min-w-0 flex-1 truncate">
              {proximaAcaoFollowUpCard.label}
            </span>
          </div>
        ) : null}
      </div>
    </button>
  );
}

function FollowUpConversationItem({
  conversa,
  selected,
  canalStyle,
  canal,
  nome,
  nomeWhatsapp = "",
  nomePrincipalEhWhatsapp = false,
  mostrarNomeWhatsappSecundario = false,
  mensagemPreview,
  leadResumo,
  linhaResumoCard,
  nomeConexao = "",
  mostrarConexao = false,
  agora,
  onClick,
}) {
  const telefone = formatarTelefoneCard(
    conversa.numero || conversa.telefone || nome,
  );
  const followUp = getTempoFollowUpCard(conversa, agora) || {};
  const draMaluAtiva = isDraMaluEmRecuperacaoCard(conversa);
  const recuperadoDraMalu = isRecuperadoDraMaluCard(conversa);
  const etapaNumero = Number(
    conversa.dra_malu_etapa || conversa._follow_up_etapa || conversa.followup_ordem || 0,
  );
  const totalEtapas = Number(
    conversa.dra_malu_total_etapas || conversa._follow_up_total || conversa._followup_total_regua || 0,
  );
  const etapa =
    conversa.dra_malu_etapa_titulo ||
    followUp.etapaLabel ||
    conversa._follow_up_etapa_label ||
    conversa._follow_up_titulo ||
    conversa.status_followup ||
    "Follow-up";
  const vencimento = formatarDataFollowUpCard(
    conversa._follow_up_liberado_em ||
      conversa.followup_liberado_em ||
      conversa.agendado_para ||
      conversa.agendadoPara ||
      conversa.janela_inicio ||
      conversa.created_at ||
      conversa.hora,
  );
  const atraso = getAtrasoFollowUpCard(conversa, agora);
  const atrasado = !draMaluAtiva && atraso?.atrasado;
  const responsavel = nomeCurtoCard(
    conversa.dra_malu_captador_origem_nome ||
      leadResumo?.responsavelChamado ||
      conversa.responsavel_nome ||
      conversa.captador_nome ||
      conversa.usuario_responsavel_nome ||
      "",
  );
  const responsavelAgoraDraMalu = responsavel || "captador";
  const entrouEm = parseTimestamp(
    conversa.dra_malu_entrou_em || conversa.criado_em || conversa.created_at || "",
  );
  const tempoComIa = entrouEm
    ? formatarTempoEspera(Math.max(0, Number(agora || Date.now()) - entrouEm), true)
    : "";
  const textoPreview = draMaluAtiva
    ? `Dra. Malu está conduzindo a recuperação${etapaNumero ? ` · ${etapa}` : ""}`
    : recuperadoDraMalu
      ? `🟡 Recuperado pela Dra. Malu · Agora com ${responsavelAgoraDraMalu}`
      : limitarTextoCard(
          conversa._follow_up_texto || mensagemPreview || followUp.descricao || "",
          132,
        );
  const tipoAcao = draMaluAtiva ? "Automático" : getTipoAcaoFollowUpCard(conversa);
  const ordem = getOrdemFollowUpCard(conversa) || etapaNumero;

  const tema = draMaluAtiva
    ? {
        selected: "border-violet-600 bg-gradient-to-br from-violet-50 via-white to-indigo-50 shadow-[0_12px_28px_rgba(109,40,217,0.20)] ring-2 ring-violet-200",
        idle: "border-violet-300 bg-gradient-to-br from-violet-50 via-white to-indigo-50 shadow-[0_8px_24px_rgba(109,40,217,0.12)] ring-1 ring-violet-100 hover:-translate-y-0.5 hover:border-violet-500 hover:ring-violet-200",
        line: "bg-violet-600",
      }
    : recuperadoDraMalu? {
          selected: "border-amber-500 bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 shadow-[0_12px_26px_rgba(245,158,11,0.20)] ring-2 ring-amber-300",
          idle: "border-amber-500 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-100 shadow-[0_10px_26px_rgba(245,158,11,0.20)] ring-2 ring-amber-200 hover:-translate-y-0.5 hover:border-amber-600 hover:ring-amber-300",
          line: "bg-amber-500",
        }
      : {
          selected: "border-rose-500 bg-white shadow-[0_12px_26px_rgba(244,63,94,0.16)] ring-2 ring-rose-100",
          idle: atrasado
            ? "border-rose-300 bg-white shadow-[0_8px_22px_rgba(244,63,94,0.12)] ring-1 ring-rose-100 hover:-translate-y-0.5 hover:border-rose-400 hover:ring-rose-200"
            : "border-orange-200 bg-white shadow-[0_6px_18px_rgba(249,115,22,0.10)] ring-1 ring-orange-100 hover:-translate-y-0.5 hover:border-orange-300 hover:ring-orange-200",
          line: atrasado ? "bg-rose-500" : "bg-orange-500",
        };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative w-full overflow-hidden rounded-[16px] border px-2 py-2 text-left transition ${
        selected ? tema.selected : tema.idle
      }`}
    >
      <div className={`absolute bottom-0 left-0 top-0 w-1 ${tema.line}`} />

      <div className="flex items-start gap-2 pl-0.5">
        <div className="relative shrink-0">
          {conversa.foto || conversa.profileUrl || conversa.avatar_url ? (
            <img
              src={conversa.foto || conversa.profileUrl || conversa.avatar_url}
              alt={nome}
              className={`h-9 w-9 rounded-full object-cover ring-2 ${
                draMaluAtiva ? "ring-violet-200" : selected ? canalStyle.avatarRing : "ring-orange-200"
              }`}
            />
          ) : (
            <div
              className={`flex h-9 w-9 items-center justify-center rounded-full ring-2 ${
                draMaluAtiva
                  ? "bg-violet-50 text-violet-600 ring-violet-200"
                  : selected
                    ? canalStyle.avatarBg
                    : "bg-orange-50 text-orange-400 ring-orange-200"
              }`}
            >
              <FaUserCircle size={22} />
            </div>
          )}

          <span
            className={`absolute -bottom-0.5 -right-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-white shadow-sm ${
              draMaluAtiva ? "bg-violet-600 text-white" : canalStyle.iconBg
            }`}
            title={draMaluAtiva ? "Dra. Malu IA" : canal}
          >
            {draMaluAtiva ? (
              <img
                src={DRA_MALU_AVATAR_URL}
                alt="Dra. Malu"
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              <CanalIcon canal={canal} />
            )}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-1.5">
                <div
                  className={`min-w-0 truncate text-[12px] font-black leading-tight ${
                    draMaluAtiva
                      ? "text-violet-950"
                      : recuperadoDraMalu
                        ? "text-amber-950"
                        : selected
                          ? "text-rose-950"
                          : "text-slate-950"
                  }`}
                  title={nome}
                >
                  {nome}
                </div>

                {nomePrincipalEhWhatsapp ? (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-emerald-700">
                    <FaWhatsapp size={8} /> WhatsApp
                  </span>
                ) : null}

                {mostrarConexao && nomeConexao ? (
                  <span
                    className="inline-flex max-w-[82px] shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-indigo-700"
                    title={`Conexão: ${nomeConexao}`}
                  >
                    <span className="truncate">{nomeConexao}</span>
                  </span>
                ) : null}
              </div>

              {mostrarNomeWhatsappSecundario ? (
                <div className="mt-px flex min-w-0 items-center gap-1 text-[9.5px] font-bold text-emerald-700">
                  <FaWhatsapp size={9} className="shrink-0" />
                  <span className="min-w-0 truncate">WhatsApp: {nomeWhatsapp}</span>
                </div>
              ) : null}

              <div className="mt-px truncate text-[10px] font-semibold text-slate-500">
                {telefone || conversa.numero || conversa.telefone || "Telefone não informado"}
              </div>
            </div>

            <span
              className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide ring-1 ${
                draMaluAtiva
                  ? "bg-violet-100 text-violet-800 ring-violet-300"
                  : recuperadoDraMalu
                    ? "bg-amber-100 text-amber-900 ring-amber-300"
                    : atrasado
                      ? "bg-rose-50 text-rose-700 ring-rose-200"
                      : "bg-orange-50 text-orange-700 ring-orange-200"
              }`}
              title={
                draMaluAtiva
                  ? DRA_MALU_EM_RECUPERACAO_TITULO
                  : recuperadoDraMalu
                    ? DRA_MALU_RECUPERADO_TITULO
                    : atraso?.label || "Follow-up vencido"
              }
            >
              {draMaluAtiva ? (
                <img
                  src={DRA_MALU_AVATAR_URL}
                  alt="Dra. Malu"
                  className="h-3.5 w-3.5 rounded-full object-cover ring-1 ring-violet-200"
                />
              ) : recuperadoDraMalu ? (
                <Sparkles size={10} />
              ) : (
                <RotateCcw size={10} />
              )}
              {draMaluAtiva
                ? "Dra. Malu"
                : recuperadoDraMalu
                  ? `Agora com ${responsavelAgoraDraMalu}`
                  : atrasado
                    ? "Vencido"
                    : "Pendente"}
            </span>
          </div>

          {draMaluAtiva ? (
            <div className="mt-1 flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-100/80 px-2 py-1 text-[9.5px] font-black text-violet-900">
              <img
                src={DRA_MALU_AVATAR_URL}
                alt="Dra. Malu"
                className="h-4 w-4 shrink-0 rounded-full object-cover ring-1 ring-violet-200"
              />
              <span className="truncate">DRA. MALU · EM RECUPERAÇÃO</span>
            </div>
          ) : null}

          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1">
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[9px] font-black leading-3 ${
                draMaluAtiva
                  ? "border-violet-200 bg-violet-50 text-violet-700"
                  : "border-rose-200 bg-rose-50 text-rose-700"
              }`}
            >
              <BellRing size={10} />
              {draMaluAtiva && totalEtapas
                ? `Etapa ${Math.max(1, ordem)} de ${totalEtapas}`
                : ordem
                  ? `Etapa ${ordem}`
                  : etapa}
            </span>

            {ordem || draMaluAtiva ? (
              <span className="inline-flex min-w-0 max-w-[145px] items-center rounded-full border border-slate-200 bg-white px-1.5 py-px text-[9px] font-bold leading-3 text-slate-600">
                <span className="truncate">{etapa}</span>
              </span>
            ) : null}

            {draMaluAtiva && tempoComIa ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-white px-1.5 py-px text-[9px] font-bold leading-3 text-violet-700">
                <Clock3 size={10} /> IA há {tempoComIa}
              </span>
            ) : vencimento && !draMaluAtiva ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-1.5 py-px text-[9px] font-bold leading-3 text-slate-600">
                <Clock3 size={10} /> {vencimento}
              </span>
            ) : null}

            {tipoAcao ? (
              <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-1.5 py-px text-[9px] font-bold leading-3 text-blue-700">
                {tipoAcao}
              </span>
            ) : null}
          </div>

          {textoPreview ? (
            <div
              className={`mt-1 line-clamp-1 rounded-lg border px-2 py-1 text-[9.5px] leading-3 ${
                draMaluAtiva
                  ? "border-violet-200 bg-white/90 font-semibold text-violet-800"
                  : recuperadoDraMalu
                    ? "border-amber-300 bg-amber-100 font-black text-amber-900 shadow-sm ring-1 ring-amber-200"
                    : "border-slate-200 bg-slate-50 font-medium text-slate-600"
              }`}
              title={textoPreview}
            >
              {textoPreview}
            </div>
          ) : null}

          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1">
            {responsavel ? (
              <span className="inline-flex min-w-0 max-w-[165px] items-center gap-1 rounded-full border border-slate-200 bg-white px-1.5 py-px text-[9px] font-bold leading-3 text-slate-600">
                <UserRound size={10} />
                <span className="truncate">
                  {draMaluAtiva ? `Origem: ${responsavel}` : `Resp.: ${responsavel}`}
                </span>
              </span>
            ) : null}

            {linhaResumoCard && !draMaluAtiva ? (
              <span className="inline-flex min-w-0 max-w-[175px] items-center gap-1 rounded-full border border-slate-200 bg-white px-1.5 py-px text-[9px] font-bold leading-3 text-slate-500">
                <span className="truncate">{linhaResumoCard}</span>
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </button>
  );
}

function getProximaAcaoFollowUpSutilCard(
  conversa = {},
  agora = Date.now(),
  followUpRegras = [],
) {
  if (!temFollowUpRealCard(conversa)) return null;

  const fluxo = getFluxoFollowUpCard(conversa);
  const realizados = fluxo.filter((item) => {
    const status = normalizar(item.status);
    return status.includes("realiz") || status.includes("execut");
  });

  const ultimaOrdemRealizada = Math.max(
    0,
    ...realizados.map((item) => Number(item.ordem || item.followup_ordem || 0)),
  );

  const atualFluxo =
    fluxo.find((item) => {
      const ordem = Number(item.ordem || item.followup_ordem || 0);
      const status = normalizar(item.status);
      return (
        ordem > ultimaOrdemRealizada &&
        !status.includes("realiz") &&
        !status.includes("execut") &&
        !status.includes("cancel")
      );
    }) || null;

  const atual =
    atualFluxo ||
    (conversa._follow_up_pendente ||
    conversa._followup_chamado_id ||
    conversa._follow_up_id ||
    conversa.followup_id ||
    conversa.chamado_followup_id
      ? conversa
      : null);

  if (!atual) return null;

  const status = normalizar(
    atual.status || atual.status_followup || atual._follow_up_status || "",
  );

  if (
    status.includes("cancel") ||
    status.includes("realiz") ||
    status.includes("execut")
  ) {
    return null;
  }

  const ordem =
    Number(
      atual.ordem ||
        atual.followup_ordem ||
        atual._follow_up_ordem ||
        atual._follow_up_etapa ||
        conversa._follow_up_ordem ||
        conversa.followup_ordem ||
        0,
    ) || null;

  const total = getTotalEtapasFollowUpCard(conversa, fluxo, followUpRegras);
  const canal = getCanalFollowUpCard(atual);
  const agendadoPara = getPrimeiroTexto([
    atual.agendado_para,
    atual._follow_up_liberado_em,
    atual.followup_liberado_em,
    atual.janela_inicio,
    atual.liberado_em,
    atual.proxima_execucao_em,
    conversa._follow_up_liberado_em,
    conversa.followup_liberado_em,
    conversa.agendado_para,
    conversa.janela_inicio,
  ]);
  const agendadoTimestamp = parseTimestamp(agendadoPara);
  const vencida = agendadoTimestamp
    ? agendadoTimestamp <= Number(agora || Date.now())
    : status.includes("pend") ||
      status.includes("venc") ||
      Boolean(conversa._follow_up_pendente);
  const horario = vencida
    ? "vencida"
    : formatarHoraFollowUpSutilCard(agendadoPara) || "programada";
  const etapa = ordem
    ? `etapa ${ordem}/${total || ordem}`
    : total
      ? `etapa ?/${total}`
      : "etapa";

  return {
    label: `Próx. ação: ${canal} · ${etapa} · ${horario}`,
    title: `Próxima ação do follow-up: ${canal}${ordem ? ` · etapa ${ordem}/${total || ordem}` : ""}${agendadoPara ? ` · libera em ${formatarDataFollowUpCard(agendadoPara)}` : ""}`,
    className: vencida ? "text-amber-800" : "text-slate-700",
    ribbonClass: vencida
      ? "border-amber-700 bg-amber-700 text-white"
      : "border-slate-700 bg-slate-700 text-white",
    microTagClass: vencida ? "bg-amber-700" : "bg-slate-700",
  };
}

function getTotalEtapasFollowUpCard(
  conversa = {},
  fluxo = [],
  followUpRegras = [],
) {
  const regrasExternas = Array.isArray(followUpRegras) ? followUpRegras : [];

  const candidatos = [
    conversa.total_regua,
    conversa.total_regras,
    conversa.total_regua_followup,
    conversa.total_regras_followup,
    conversa.total_etapas_followup,
    conversa.quantidade_followups,
    conversa.followup_total_regras,
    conversa._follow_up_total_regras,
    conversa._followup_total_regua,
    conversa._follow_up_regua_total,
    conversa.regua_followup_total,
    regrasExternas.length,
    ...regrasExternas.map((item) =>
      Number(item?.followup_ordem || item?.ordem || 0),
    ),
  ];

  const regrasInternas =
    conversa._followup_regras ||
    conversa._follow_up_regras ||
    conversa.followup_regras ||
    conversa.regras_followup ||
    [];

  if (Array.isArray(regrasInternas)) {
    candidatos.push(
      regrasInternas.length,
      ...regrasInternas.map((item) =>
        Number(item?.followup_ordem || item?.ordem || 0),
      ),
    );
  }

  if (Array.isArray(fluxo)) {
    candidatos.push(
      fluxo.length,
      ...fluxo.map((item) => Number(item?.followup_ordem || item?.ordem || 0)),
    );
  }

  const validos = candidatos
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  return validos.length ? Math.max(...validos) : 1;
}

function formatarHoraFollowUpSutilCard(value) {
  const timestamp = parseTimestamp(value);
  if (!timestamp) return "";

  const data = new Date(timestamp);
  if (Number.isNaN(data.getTime())) return "";

  const hoje = new Date();
  const mesmoDia =
    data.getFullYear() === hoje.getFullYear() &&
    data.getMonth() === hoje.getMonth() &&
    data.getDate() === hoje.getDate();

  if (mesmoDia) {
    return data.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getFollowUpResumoOperacionalCard(conversa = {}, agora = Date.now()) {
  if (!temFollowUpRealCard(conversa)) return null;

  const resumoFluxo = getFollowUpResumoPorFluxoCard(conversa, agora);
  if (resumoFluxo) return resumoFluxo;

  const mensagens = Array.isArray(conversa.mensagens) ? conversa.mensagens : [];
  const candidatos = [];

  const adicionar = (obj = {}, origem = "") => {
    if (!obj || typeof obj !== "object") return;

    const titulo = getPrimeiroTexto([
      obj.followup_ultimo_titulo,
      obj.followup_titulo,
      obj.titulo_followup,
      obj._follow_up_ultimo_titulo,
      obj._follow_up_titulo,
      obj._follow_up_etapa_label,
      obj.status_followup,
      obj.titulo,
    ]);
    const ordem = getPrimeiroTexto([
      obj.followup_ordem,
      obj._follow_up_ordem,
      obj._follow_up_etapa,
    ]);
    const status = normalizar(
      getPrimeiroTexto([
        obj.followup_status,
        obj.status_followup,
        obj._follow_up_status,
        obj.status,
        obj.resultado,
      ]),
    );
    const temIdFollowUpReal = Boolean(
      obj._followup_chamado_id ||
      obj._follow_up_id ||
      obj.followup_id ||
      obj.followupId ||
      obj.id_followup ||
      obj.chamado_followup_id,
    );

    const temStatusOperacional =
      status.includes("pend") ||
      status.includes("venc") ||
      status.includes("realiz") ||
      status.includes("execut") ||
      status.includes("cancel");

    const temExecucaoReal = Boolean(
      obj.realizado_em ||
      obj.followup_realizado_em ||
      obj._follow_up_realizado_em ||
      obj.executado_em,
    );

    const temMarcador = Boolean(
      temIdFollowUpReal ||
      temStatusOperacional ||
      temExecucaoReal ||
      obj._follow_up_pendente,
    );

    if (!temMarcador) return;

    const data = getPrimeiroTexto([
      obj.realizado_em,
      obj.followup_realizado_em,
      obj._follow_up_realizado_em,
      obj.executado_em,
      obj.agendado_para,
      obj._follow_up_liberado_em,
      obj.datahora,
      obj.updated_at,
      obj.created_at,
    ]);
    const statusFinal =
      status.includes("realiz") ||
      status.includes("execut") ||
      obj.resultado === "mensagem_enviada"
        ? "realizado"
        : status.includes("cancel")
          ? "cancelado"
          : status.includes("pend") ||
              status.includes("venc") ||
              obj._follow_up_pendente
            ? "pendente"
            : origem === "mensagem" && titulo
              ? "realizado"
              : "registrado";

    const agendadoPara = getPrimeiroTexto([
      obj.agendado_para,
      obj._follow_up_liberado_em,
      obj.followup_liberado_em,
      obj.janela_inicio,
    ]);
    const realizadoEm = getPrimeiroTexto([
      obj.realizado_em,
      obj.followup_realizado_em,
      obj._follow_up_realizado_em,
      obj.executado_em,
    ]);

    candidatos.push({
      titulo: titulo || (ordem ? `Follow-up ${ordem}` : "Follow-up"),
      ordem,
      status: statusFinal,
      data,
      timestamp: parseTimestamp(data),
      agendadoPara,
      agendadoTimestamp: parseTimestamp(agendadoPara),
      realizadoEm,
      realizadoTimestamp: parseTimestamp(realizadoEm),
      resultado: obj.resultado || "",
    });
  };

  adicionar(conversa, "conversa");
  mensagens.forEach((mensagem) => adicionar(mensagem, "mensagem"));

  if (!candidatos.length) return null;

  const prioridade = { pendente: 4, realizado: 3, registrado: 2, cancelado: 1 };
  const item = candidatos.sort((a, b) => {
    const pa = prioridade[a.status] || 0;
    const pb = prioridade[b.status] || 0;
    if (pa !== pb) return pb - pa;
    return (b.timestamp || 0) - (a.timestamp || 0);
  })[0];

  const dataLabel = item.data ? formatarDataFollowUpCard(item.data) : "";
  const tituloCurto =
    item.titulo || (item.ordem ? `Follow-up ${item.ordem}` : "Follow-up");
  const vencido =
    item.status === "pendente" &&
    item.agendadoTimestamp &&
    item.agendadoTimestamp <= Number(agora || Date.now());
  const futuro =
    item.status === "pendente" &&
    item.agendadoTimestamp &&
    item.agendadoTimestamp > Number(agora || Date.now());
  const tempoDesdeRealizado = item.realizadoTimestamp
    ? formatarTempoRelativoFollowup(
        Number(agora || Date.now()) - item.realizadoTimestamp,
      )
    : "";
  const tempoDesdeAgendado = item.agendadoTimestamp
    ? formatarTempoRelativoFollowup(
        Math.abs(Number(agora || Date.now()) - item.agendadoTimestamp),
      )
    : "";

  if (item.status === "realizado") {
    return {
      status: "realizado",
      label: `${tituloCurto} realizado${tempoDesdeRealizado ? ` · há ${tempoDesdeRealizado}` : dataLabel ? ` · ${dataLabel}` : ""}`,
      title: `Fluxo de follow-up: ${tituloCurto} realizado${dataLabel ? ` em ${dataLabel}` : ""}`,
      className: "bg-emerald-50 text-emerald-800 ring-emerald-200",
      cardClass:
        "border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 shadow-[0_8px_22px_rgba(16,185,129,0.10)] ring-1 ring-emerald-100 hover:-translate-y-0.5 hover:border-emerald-400 hover:ring-emerald-200",
      accentClass: "bg-emerald-500",
      topBadgeClass: "bg-emerald-500 shadow-emerald-900/20",
      icon: <CheckCheck size={11} className="shrink-0 text-emerald-600" />,
    };
  }

  if (item.status === "pendente") {
    if (vencido) {
      return {
        status: "pendente_vencido",
        label: `${tituloCurto} vencido${tempoDesdeAgendado ? ` · há ${tempoDesdeAgendado}` : dataLabel ? ` · ${dataLabel}` : ""}`,
        title: `Fluxo de follow-up: ${tituloCurto} vencido${dataLabel ? ` desde ${dataLabel}` : ""}`,
        className: "bg-red-50 text-red-800 ring-red-200",
        cardClass:
          "border-red-500 bg-gradient-to-br from-red-50 via-white to-rose-50 shadow-[0_10px_28px_rgba(220,38,38,0.18)] ring-2 ring-red-100 hover:-translate-y-0.5 hover:border-red-600 hover:ring-red-200",
        accentClass: "bg-red-600",
        topBadgeClass: "bg-red-600 shadow-red-900/20",
        icon: <BellRing size={11} className="shrink-0 text-red-600" />,
      };
    }

    return {
      status: futuro ? "pendente_futuro" : "pendente",
      label: `${tituloCurto} programado${tempoDesdeAgendado ? ` · vence em ${tempoDesdeAgendado}` : dataLabel ? ` · ${dataLabel}` : ""}`,
      title: `Fluxo de follow-up: ${tituloCurto} programado${dataLabel ? ` para ${dataLabel}` : ""}`,
      className: "bg-amber-50 text-amber-800 ring-amber-200",
      cardClass:
        "border-amber-400 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-[0_8px_24px_rgba(245,158,11,0.14)] ring-2 ring-amber-100 hover:-translate-y-0.5 hover:border-amber-500 hover:ring-amber-200 hover:shadow-[0_12px_28px_rgba(245,158,11,0.18)]",
      accentClass: "bg-amber-500",
      topBadgeClass: "bg-amber-500 shadow-amber-900/20",
      icon: <Clock3 size={11} className="shrink-0 text-amber-600" />,
    };
  }

  if (item.status === "cancelado") {
    return null;
  }

  return null;
}

function getFollowUpResumoPorFluxoCard(conversa = {}, agora = Date.now()) {
  const fluxo = getFluxoFollowUpCard(conversa);

  if (!fluxo.length) return null;

  const realizados = fluxo.filter((item) => {
    const status = normalizar(item.status);
    return status.includes("realiz") || status.includes("execut");
  });

  const ultimaOrdemRealizada = Math.max(
    0,
    ...realizados.map((item) => Number(item.ordem || item.followup_ordem || 0)),
  );

  const atual =
    fluxo.find((item) => {
      const ordem = Number(item.ordem || item.followup_ordem || 0);
      const status = normalizar(item.status);
      return (
        ordem > ultimaOrdemRealizada &&
        !status.includes("realiz") &&
        !status.includes("execut") &&
        !status.includes("cancel")
      );
    }) ||
    fluxo.find(
      (item) =>
        Number(item.ordem || item.followup_ordem || 0) > ultimaOrdemRealizada,
    );

  if (!atual) return null;

  const ordem = Number(atual.ordem || atual.followup_ordem || 0);
  const titulo =
    atual.titulo || `Follow-up ${ordem || ""}`.trim() || "Follow-up";
  const canal = getCanalFollowUpCard(atual);
  const agendadoPara =
    atual.agendado_para ||
    atual._follow_up_liberado_em ||
    atual.followup_liberado_em ||
    atual.janela_inicio ||
    "";

  const agendadoTimestamp = parseTimestamp(agendadoPara);
  const status = normalizar(atual.status);

  // Follow-up cancelado é histórico. Não deve pintar o card como ação ativa.
  if (status.includes("cancel")) return null;

  const vencido =
    agendadoTimestamp && agendadoTimestamp <= Number(agora || Date.now());
  const futuro =
    agendadoTimestamp && agendadoTimestamp > Number(agora || Date.now());

  const tituloCurto = ordem
    ? `Follow-up ${ordem} · ${canal}`
    : `${titulo} · ${canal}`;

  if (status.includes("realiz") || status.includes("execut")) {
    const realizadoEm =
      atual.realizado_em ||
      atual.executado_em ||
      atual.updated_at ||
      atual.created_at ||
      "";

    const realizadoTimestamp = parseTimestamp(realizadoEm);
    const tempo = realizadoTimestamp
      ? formatarTempoRelativoFollowup(
          Number(agora || Date.now()) - realizadoTimestamp,
        )
      : "";

    return {
      status: "realizado",
      label: `${tituloCurto} realizado${tempo ? ` · há ${tempo}` : ""}`,
      title: `${tituloCurto} realizado`,
      className: "bg-emerald-50 text-emerald-800 ring-emerald-200",
      cardClass:
        "border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 shadow-[0_8px_22px_rgba(16,185,129,0.10)] ring-1 ring-emerald-100 hover:-translate-y-0.5 hover:border-emerald-400 hover:ring-emerald-200",
      accentClass: "bg-emerald-500",
      topBadgeClass: "bg-emerald-500 shadow-emerald-900/20",
      icon: <CheckCheck size={11} className="shrink-0 text-emerald-600" />,
    };
  }

  if (vencido) {
    const tempo = agendadoTimestamp
      ? formatarTempoRelativoFollowup(
          Number(agora || Date.now()) - agendadoTimestamp,
        )
      : "";

    return {
      status: "pendente_vencido",
      label: `${tituloCurto} vencido${tempo ? ` · há ${tempo}` : ""}`,
      title: `${tituloCurto} vencido`,
      className: "bg-red-50 text-red-800 ring-red-200",
      cardClass:
        "border-red-500 bg-gradient-to-br from-red-50 via-white to-rose-50 shadow-[0_10px_28px_rgba(220,38,38,0.18)] ring-2 ring-red-100 hover:-translate-y-0.5 hover:border-red-600 hover:ring-red-200",
      accentClass: "bg-red-600",
      topBadgeClass: "bg-red-600 shadow-red-900/20",
      icon: <BellRing size={11} className="shrink-0 text-red-600" />,
    };
  }

  if (futuro) {
    const tempo = formatarTempoRelativoFollowup(
      agendadoTimestamp - Number(agora || Date.now()),
    );
    const data = formatarDataFollowUpCard(agendadoPara);

    return {
      status: "aguardando",
      label: `${tituloCurto} programado${tempo ? ` · libera em ${tempo}` : data ? ` · ${data}` : ""}`,
      title: `${tituloCurto} libera em ${data || "data programada"}`,
      className: "bg-amber-50 text-amber-800 ring-amber-200",
      cardClass:
        "border-amber-300 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-[0_8px_22px_rgba(245,158,11,0.12)] ring-1 ring-amber-100 hover:-translate-y-0.5 hover:border-amber-400 hover:ring-amber-200",
      accentClass: "bg-amber-500",
      topBadgeClass: "bg-amber-500 shadow-amber-900/20",
      icon: <Clock3 size={11} className="shrink-0 text-amber-600" />,
    };
  }

  return null;
}

function getFluxoFollowUpCard(conversa = {}) {
  if (!temFollowUpRealCard(conversa)) return [];

  const listas = [
    conversa._followup_fluxo_completo,
    conversa._followups_fluxo,
    conversa._follow_up_fluxo,
    conversa._followups_historico,
    conversa._follow_up_historico,
    conversa.followups_historico,
    conversa.chamado_followups,
  ];

  const fluxo = [];

  for (const lista of listas) {
    if (!Array.isArray(lista) || lista.length === 0) continue;

    for (const item of lista) {
      if (!item || typeof item !== "object") continue;

      const ordem = Number(
        item._follow_up_ordem ||
          item.followup_ordem ||
          item.ordem ||
          item.etapa ||
          0,
      );

      const id =
        item._follow_up_id ||
        item._followup_chamado_id ||
        item.followup_id ||
        item.id ||
        null;

      if (!ordem && !id) continue;

      const statusRaw =
        item._follow_up_status ||
        item.status_followup ||
        item.status ||
        item.situacao ||
        "futura";

      fluxo.push({
        ...item,
        id,
        ordem,
        followup_ordem: ordem,
        status: statusRaw,
        canal:
          item._follow_up_canal ||
          item.followup_canal ||
          item.canal ||
          item.tipo_canal ||
          item.tipo_acao ||
          "",
        titulo:
          item._follow_up_titulo ||
          item.titulo ||
          item.nome ||
          item.label ||
          `Follow-up ${ordem || ""}`.trim(),
        agendado_para:
          item._follow_up_liberado_em ||
          item.agendado_para ||
          item.janela_inicio ||
          item.liberado_em ||
          item.proxima_execucao_em ||
          item.created_at ||
          "",
        realizado_em:
          item._follow_up_realizado_em ||
          item.realizado_em ||
          item.executado_em ||
          "",
      });
    }
  }

  const porOrdem = new Map();

  for (const item of fluxo) {
    const ordem = Number(item.ordem || item.followup_ordem || 0);
    if (!ordem) continue;

    const atual = porOrdem.get(ordem);
    const status = normalizar(item.status);

    const prioridade =
      status.includes("realiz") || status.includes("execut")
        ? 5
        : status.includes("pend") || status.includes("andamento")
          ? 4
          : status.includes("cancel")
            ? 3
            : item.agendado_para
              ? 2
              : 1;

    const prioridadeAtual = atual
      ? normalizar(atual.status).includes("realiz") ||
        normalizar(atual.status).includes("execut")
        ? 5
        : normalizar(atual.status).includes("pend") ||
            normalizar(atual.status).includes("andamento")
          ? 4
          : normalizar(atual.status).includes("cancel")
            ? 3
            : atual.agendado_para
              ? 2
              : 1
      : 0;

    if (!atual || prioridade >= prioridadeAtual) {
      porOrdem.set(ordem, item);
    }
  }

  return [...porOrdem.values()].sort(
    (a, b) => Number(a.ordem || 0) - Number(b.ordem || 0),
  );
}

function temFollowUpRealCard(conversa = {}) {
  const temMarcadorDireto = Boolean(
    conversa._follow_up_pendente ||
    conversa._followup_chamado_id ||
    conversa._follow_up_id ||
    conversa.followup_id ||
    conversa.followupId ||
    conversa.id_followup ||
    conversa.chamado_followup_id,
  );

  if (temMarcadorDireto) return true;

  const listas = [
    conversa._followup_fluxo_completo,
    conversa._followups_fluxo,
    conversa._follow_up_fluxo,
    conversa._followups_historico,
    conversa._follow_up_historico,
    conversa.followups_historico,
    conversa.chamado_followups,
  ];

  return listas.some((lista) => {
    if (!Array.isArray(lista) || lista.length === 0) return false;

    return lista.some((item) => {
      if (!item || typeof item !== "object") return false;

      const status = normalizar(
        item._follow_up_status ||
          item.status_followup ||
          item.status ||
          item.situacao ||
          "",
      );

      return Boolean(
        item._existe_no_banco ||
        item._followup_chamado_id ||
        item._follow_up_id ||
        item.followup_id ||
        item.followupId ||
        item.id_followup ||
        item.chamado_followup_id ||
        item.realizado_em ||
        item.executado_em ||
        item.followup_realizado_em ||
        item._follow_up_realizado_em ||
        item._follow_up_pendente ||
        status.includes("pend") ||
        status.includes("venc") ||
        status.includes("realiz") ||
        status.includes("execut") ||
        status.includes("cancel"),
      );
    });
  });
}

function getCanalFollowUpCard(item = {}) {
  const raw = normalizar(
    item.canal ||
      item.followup_canal ||
      item._follow_up_canal ||
      item.tipo_canal ||
      item.tipo_acao ||
      item.titulo ||
      "",
  );

  if (
    raw.includes("ligacao") ||
    raw.includes("telefon") ||
    raw.includes("call")
  ) {
    return "Ligação";
  }

  return "WhatsApp";
}

function formatarTempoRelativoFollowup(ms) {
  const totalMinutos = Math.max(0, Math.floor(Number(ms || 0) / 60000));

  if (!Number.isFinite(totalMinutos)) return "";
  if (totalMinutos < 1) return "agora";
  if (totalMinutos < 60) return `${totalMinutos}m`;

  const horas = Math.floor(totalMinutos / 60);
  const minutos = totalMinutos % 60;

  if (horas < 24) {
    return minutos ? `${horas}h${minutos}m` : `${horas}h`;
  }

  const dias = Math.floor(horas / 24);
  const horasRestantes = horas % 24;

  return horasRestantes ? `${dias}d${horasRestantes}h` : `${dias}d`;
}

function getDataEntradaCaptaZapCard(conversa = {}) {
  const mensagens = Array.isArray(conversa.mensagens) ? conversa.mensagens : [];

  const primeiraMensagemTimestamp = mensagens
    .map(getMensagemTimestamp)
    .filter((timestamp) => Number.isFinite(timestamp) && timestamp > 0)
    .sort((a, b) => a - b)[0];

  if (primeiraMensagemTimestamp) return primeiraMensagemTimestamp;

  return parseTimestamp(
    conversa.data_entrada_captazap ||
      conversa.entrada_captazap_em ||conversa.captazap_entrada_em ||
      conversa.primeira_mensagem_em ||
      conversa.data_primeira_mensagem ||
      conversa.first_message_at ||
      conversa.iniciado_em ||
      conversa.aberto_em ||
      conversa.created_at ||
      conversa.lead?.data_entrada_captazap ||
      conversa.lead?.primeira_mensagem_em ||
      conversa.lead?.created_at ||
      "",
  );
}

function formatarEntradaCaptaZapCard(conversa = {}, { curto = true } = {}) {
  const timestamp = getDataEntradaCaptaZapCard(conversa);

  if (!timestamp) return "";

  const data = new Date(timestamp);
  if (Number.isNaN(data.getTime())) return "";

  const opcoes = curto
    ? {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }
    : {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };

  return data.toLocaleString("pt-BR", opcoes);
}

function getFonteOuCampanhaCard(conversa = {}, leadResumo = {}) {
  const lead = leadResumo?.lead || conversa?.lead || {};

  const valor = getPrimeiroTexto([
    lead.nome_fonte,
    lead.fonte,
    lead.fonte_lead,
    lead.nome_fonte_lead,
    lead.nome_campanha,
    lead.campanha,
    lead.campanha_nome,
    lead.utm_campaign,
    lead.origem_campanha,
    conversa.nome_fonte,
    conversa.fonte,
    conversa.fonte_lead,
    conversa.nome_fonte_lead,
    conversa.nome_campanha,
    conversa.campanha,
    conversa.campanha_nome,
    conversa.utm_campaign,
    conversa.origem_campanha,
    lead.origem,
    conversa.origem,
  ]);

  return limparFonteOperacionalCard(valor);
}

function limparFonteOperacionalCard(valor = "") {
  const texto = String(valor || "").trim();
  if (!texto) return "";

  const normalizado = normalizar(texto);
  const canaisGenericos = new Set([
    "whatsapp",
    "zap",
    "wpp",
    "telefone",
    "ligacao",
    "captazap",
  ]);

  if (canaisGenericos.has(normalizado)) return "";

  return texto;
}

function montarLinhaResumoOperacionalCard({
  responsavelChamado = "",
  captadorLead = "",
  fonteLead = "",
} = {}) {
  const itens = [];
  const responsavel = nomeCurtoCard(responsavelChamado);
  const captador = nomeCurtoCard(captadorLead);
  const fonte = limparTextoCard(fonteLead);

  if (responsavel) {
    itens.push(`Resp.: ${responsavel}`);
  }

  if (captador && normalizar(captador) !== normalizar(responsavel)) {
    itens.push(`Capt.: ${captador}`);
  }

  if (fonte) {
    itens.push(`Fonte: ${fonte}`);
  }

  return itens.join(" · ");
}

function nomeCurtoCard(nome = "") {
  const ignorar = new Set(["de", "da", "do", "das", "dos", "e"]);
  const partes = String(nome || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((parte, index) => index === 0 || !ignorar.has(parte.toLowerCase()));

  if (partes.length <= 2) return partes.join(" ");

  return `${partes[0]} ${partes[1]}`;
}

function limparTextoCard(valor = "") {
  return String(valor || "").trim();
}

function isReaberturaResponderCard(conversa = {}) {
  return Boolean(
    conversa.reabertura_pendente ||
    conversa.reaberturaPendente ||
    conversa.cliente_respondeu_apos_fechamento ||
    conversa.respondeu_apos_fechamento ||
    conversa.nova_mensagem_apos_fechamento ||
    conversa.novaRespostaAposFechamento ||
    conversa._fechado_com_resposta,
  );
}

function getFilaProtecaoCard(conversa = {}) {
  const valoresFlag = [
    conversa.fila_protecao,
    conversa.filaProtecao,
    conversa._fila_protecao,
  ];
  const flagAtiva = valoresFlag.some((valor) => {
    if (valor === true || valor === 1) return true;
    const texto = String(valor ?? "").trim().toLowerCase();
    return ["true", "1", "sim", "yes"].includes(texto);
  });

  const reaberturaExplicita = isReaberturaResponderCard(conversa);
  const motivoInformado = String(
    conversa.fila_protecao_motivo || conversa.filaProtecaoMotivo || "",
  )
    .trim()
    .toLowerCase();

  const motivosConhecidos = new Set([
    "retorno_apos_fechamento",
    "sem_resposta_3h",
    "recuperado_dra_malu",
  ]);

  const motivo = motivosConhecidos.has(motivoInformado)
    ? motivoInformado
    : reaberturaExplicita
      ? "retorno_apos_fechamento"
      : flagAtiva
        ? motivoInformado || "fila_protecao"
        : null;

  const ativa = Boolean(flagAtiva || reaberturaExplicita);
  const labelRecebida = String(
    conversa.fila_protecao_label || conversa.filaProtecaoLabel || "",
  ).trim();

  let label = "";
  if (ativa) {
    if (labelRecebida) {
      label = labelRecebida;
    } else if (motivo === "retorno_apos_fechamento") {
      label = "Retorno após fechamento";
    } else if (motivo === "recuperado_dra_malu") {
      label = "Recuperado pela Dra. Malu";
    } else if (motivo === "sem_resposta_3h") {
      label = "Sem resposta há mais de 3h";
    } else {
      label = "Fila de proteção";
    }
  }

  return {
    ativa,
    motivo,
    label,
  };
}

function getFilaProtecaoVisualCard(
  filaProtecao = {},
  { recuperadoDraMalu = false } = {},
) {
  const motivo = String(filaProtecao?.motivo || "").trim().toLowerCase();
  const ehDraMalu = Boolean(
    filaProtecao?.ativa &&
      (motivo === "recuperado_dra_malu" || recuperadoDraMalu),
  );

  if (ehDraMalu) {
    return {
      tipo: "dra_malu",
      label: "Recuperado pela Dra. Malu",
      topLabel: "Dra. Malu",
      accentClass: "bg-violet-600",
      cardSelectedClass:
        "border-violet-600 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 shadow-[0_12px_28px_rgba(124,58,237,0.20)] ring-2 ring-violet-200",
      cardClass:
        "border-violet-400 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 shadow-[0_8px_24px_rgba(124,58,237,0.16)] ring-1 ring-violet-200 hover:-translate-y-0.5 hover:border-violet-500 hover:ring-violet-300",
      topBadgeClass:
        "bg-violet-600 text-white shadow-violet-950/25 ring-violet-300",
      pillClass:
        "border-violet-300 bg-violet-100 text-violet-800 ring-violet-200",
    };
  }

  if (motivo === "retorno_apos_fechamento") {
    return {
      tipo: "retorno",
      label: filaProtecao?.label || "Retorno após fechamento",
      topLabel: "Retorno",
      accentClass: "bg-amber-600",
      cardSelectedClass:
        "border-amber-600 bg-gradient-to-br from-amber-50 via-white to-yellow-50 shadow-[0_12px_28px_rgba(217,119,6,0.20)] ring-2 ring-amber-200",
      cardClass:
        "border-amber-400 bg-gradient-to-br from-amber-50 via-white to-yellow-50 shadow-[0_8px_24px_rgba(217,119,6,0.16)] ring-1 ring-amber-200 hover:-translate-y-0.5 hover:border-amber-500 hover:ring-amber-300",
      topBadgeClass:
        "bg-amber-600 text-white shadow-amber-950/25 ring-amber-300",
      pillClass:
        "border-amber-300 bg-amber-100 text-amber-900 ring-amber-200",
    };
  }

  return {
    tipo: motivo === "sem_resposta_3h" ? "sem_resposta_3h" : "protecao",
    label:
      filaProtecao?.label ||
      (motivo === "sem_resposta_3h"
        ? "Sem resposta há mais de 3h"
        : "Fila de proteção"),
    topLabel: motivo === "sem_resposta_3h" ? "Proteção +3h" : "Proteção",
    accentClass: "bg-rose-600",
    cardSelectedClass:
      "border-rose-600 bg-gradient-to-br from-rose-50 via-white to-red-50 shadow-[0_12px_28px_rgba(225,29,72,0.20)] ring-2 ring-rose-200",
    cardClass:
      "border-rose-400 bg-gradient-to-br from-rose-50 via-white to-red-50 shadow-[0_8px_24px_rgba(225,29,72,0.16)] ring-1 ring-rose-200 hover:-translate-y-0.5 hover:border-rose-500 hover:ring-rose-300",
    topBadgeClass:
      "bg-rose-600 text-white shadow-rose-950/25 ring-rose-300",
    pillClass: "border-rose-300 bg-rose-100 text-rose-800 ring-rose-200",
  };
}

function getResponsavelIdOperacionalCard(conversa = {}) {
  return (
    Number(
      conversa.responsavel_id ||
        conversa.responsavelId ||
        conversa.usuario_responsavel_id ||
        conversa.usuarioResponsavelId ||
        conversa.atendente_id ||
        conversa.operador_id ||
        conversa.captador_id ||
        0,
    ) || 0
  );
}

function getUltimaMensagemBrutaConversa(conversa = {}) {
  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  if (mensagens.length > 0) {
    return [...mensagens].sort((a, b) => {
      const dataA = getMensagemTimestamp(a) || getConversaTimestamp(a) || 0;
      const dataB = getMensagemTimestamp(b) || getConversaTimestamp(b) || 0;
      return dataB - dataA;
    })[0];
  }

  const direcao =
    conversa.ultima_mensagem_direcao ||
    conversa.ultimaMensagemDirecao ||
    conversa.ultima_direcao ||
    conversa.ultimaDirecao ||
    conversa.last_direction ||
    conversa.direcao ||
    conversa.direction ||
    conversa.sentido ||
    "";

  const autor =
    conversa.ultima_mensagem_autor ||
    conversa.ultimaMensagemAutor ||
    conversa.autor ||
    conversa.de ||
    conversa.from ||
    conversa.remetente ||
    conversa.sender ||
    "";

  const texto =
    conversa.preview ||
    conversa.ultima_mensagem ||
    conversa.ultimaMensagem ||
    conversa.last_message ||
    conversa.mensagem ||
    conversa.texto ||
    "";

  if (!direcao && !autor && !texto) return null;

  return {
    ...conversa,
    direcao,
    autor,
    texto,
    datahora:
      conversa.ultimaMensagemEm ||
      conversa.ultima_mensagem_em ||
      conversa.last_message_at ||
      conversa.data_ultima_mensagem ||
      conversa.datahora ||
      conversa.timestamp ||
      conversa.updated_at ||
      conversa.created_at ||
      "",
  };
}

function deveAparecerNaFilaRealCaptaZap(conversa = {}) {
  if (!conversa || typeof conversa !== "object") return false;

  if (getFilaProtecaoCard(conversa).ativa) return true;

  if (isChamadoFechadoCard(conversa)) return false;

  const responsavelId = getResponsavelIdOperacionalCard(conversa);
  if (responsavelId > 0) return false;

  const status = normalizar(
    conversa.status_chamado ||
      conversa.chamado_status ||
      conversa.chamada_status ||
      conversa.status ||
      "",
  );

  if (
    status &&
    !status.includes("abert") &&
    !status.includes("fila") &&
    !status.includes("aguard") &&
    !status.includes("pend") &&
    !status.includes("novo")
  ) {
    return false;
  }

  const ultimaMensagem = getUltimaMensagemBrutaConversa(conversa);

  // Fila operacional = chamado/conversa sem dono que precisa ser assumido.
  // O filtro anterior ficou rígido demais porque aceitava apenas mensagem recebida
  // do cliente, derrubando casos sem responsável que ainda precisam aparecer.
  // A única coisa que NÃO pode entrar como fila é automação/sistema falando sozinha,
  // porque esse caso deve herdar dono da lead no backend ou ir para uma rotina de sem dono.
  if (ultimaMensagem && isMensagemAutomaticaSistema(ultimaMensagem)) {
    return false;
  }

  return true;
}

function isChamadoFechadoCard(conversa = {}) {
  const status = normalizar(
    conversa.status_chamado ||
      conversa.chamado_status ||
      conversa.chamada_status ||
      conversa.status ||
      "",
  );

  if (status.includes("abert") || status.includes("open")) return false;
  if (isReaberturaResponderCard(conversa)) return false;

  return status.includes("fech");
}

function getMotivoFechamentoCard(conversa = {}) {
  return (
    conversa.motivo_fechamento ||
    conversa.motivoFechamento ||
    conversa.motivo ||
    conversa.observacao_fechamento ||
    conversa.observacaoFechamento ||
    ""
  );
}

function getObservacaoFechamentoCard(conversa = {}) {
  return (
    conversa.observacao_fechamento ||
    conversa.observacaoFechamento ||
    conversa.observacao ||
    conversa.observacoes ||
    ""
  );
}

function isFechadoPorFollowupCard(conversa = {}) {
  const motivo = normalizar(
    [
      conversa.motivo_fechamento,
      conversa.motivoFechamento,
      conversa.motivo,
      conversa.observacao_fechamento,
      conversa.observacaoFechamento,
      conversa.observacao,
      conversa.cancelado_motivo,
      conversa.followup_cancelado_motivo,
      conversa._follow_up_cancelado_motivo,
    ]
      .filter(Boolean)
      .join(" "),
  );

  const tags = normalizar(
    Array.isArray(conversa.tags)
      ? conversa.tags.join(" ")
      : conversa.tags || "",
  );

  return Boolean(
    conversa._followup_fechado ||
    conversa._fechamento_followup ||
    conversa.fechamento_followup ||
    conversa.followup_fechado ||
    motivo.includes("fechamento de follow") ||
    motivo.includes("follow-up fechado") ||
    motivo.includes("followup fechado") ||
    motivo.includes("fechamento_followup") ||
    motivo.includes("follow-up") ||
    motivo.includes("followup") ||
    tags.includes("follow-up fechado") ||
    tags.includes("followup fechado"),
  );
}

function getResumoFechamentoFollowupCard(conversa = {}) {
  const motivo = getMotivoFechamentoCard(conversa);
  const observacao = getObservacaoFechamentoCard(conversa);
  const texto = limparTextoCard(
    observacao || motivo || "Justificativa registrada",
  );

  return {
    label: `Fechamento de follow-up · ${texto}`,
    title: texto
      ? `Fechamento de follow-up: ${texto}`
      : "Chamado encerrado por fechamento de follow-up",
  };
}

function isDraMaluEmRecuperacaoCard(conversa = {}) {
  if (!conversa || typeof conversa !== "object") return false;

  if (
    conversa._dra_malu_em_recuperacao === true ||
    conversa._dra_malu_em_recuperacao === 1 ||
    String(conversa._dra_malu_em_recuperacao || "").toLowerCase() === "true" ||
    conversa.dra_malu_em_recuperacao === true ||
    conversa.dra_malu_em_recuperacao === 1 ||
    String(conversa.dra_malu_em_recuperacao || "").toLowerCase() === "true" ||
    conversa._dra_malu_virtual_followup === true ||
    conversa._dra_malu_virtual_followup === 1
  ) {
    return true;
  }

  const status = normalizar(
    conversa.dra_malu_status || conversa.status_dra_malu || "",
  );

  return [
    "aguardando inicio",
    "aguardando envio",
    "enviando",
    "erro envio",
    "em andamento",
    "aguardando etapa",
    "evento enviado",
    "aguardando resposta",
    "coletando dados",
  ].includes(status);
}

function isRecuperadoDraMaluCard(conversa = {}) {
  if (!conversa || typeof conversa !== "object") return false;

  const flags = Boolean(
    conversa.recuperado_dra_malu ||
    conversa.recuperadoDraMalu ||
    conversa._recuperado_dra_malu ||
    conversa._captazap_dra_malu_recuperado ||
    conversa._captazap_recuperado_dra_malu ||
    conversa._captazap_destaque_amarelo ||
    conversa.origem_recuperacao === "dra_malu_ia" ||
    conversa.origem_recuperacao === "dra_malu" ||
    conversa.tipo_sistema === "recuperado_dra_malu" ||
    conversa.tipoSistema === "recuperado_dra_malu",
  );

  if (flags) return true;

  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  if (
    mensagens.some((mensagem) =>
      Boolean(
        mensagem.recuperado_dra_malu ||
        mensagem.recuperadoDraMalu ||
        mensagem._recuperado_dra_malu ||
        mensagem._captazap_dra_malu_recuperado ||
        mensagem._captazap_recuperado_dra_malu ||
        mensagem._captazap_destaque_amarelo ||
        mensagem.tipo_sistema === "recuperado_dra_malu" ||
        mensagem.tipoSistema === "recuperado_dra_malu",
      ),
    )
  ) {
    return true;
  }

  const textos = [
    conversa.badge_recuperacao_dra_malu,
    conversa.badgeRecuperacaoDraMalu,
    conversa.status_recuperacao,
    conversa.statusRecuperacao,
    conversa.origem_recuperacao,
    conversa.origemRecuperacao,
    conversa.preview,
    conversa.ultima_mensagem,
    conversa.ultimaMensagem,
    conversa.mensagem,
    conversa.texto,
    conversa.observacao,
    conversa.observacoes,
    conversa.motivo,
    conversa.resumo_ia,
    conversa.resumoIa,
    ...(Array.isArray(conversa.tags) ? conversa.tags : []),
    ...mensagens.flatMap((mensagem) => [
      mensagem.badge_recuperacao_dra_malu,
      mensagem.status_recuperacao,
      mensagem.origem_recuperacao,
      mensagem.tipo_sistema,
      mensagem.tipoSistema,
      mensagem.canal,
      mensagem.autor,
      mensagem.de,
      mensagem.texto,
      mensagem.mensagem,
      mensagem.preview,
      mensagem.observacao,
    ]),
  ];

  const textoNormalizado = normalizar(textos.filter(Boolean).join(" "));

  return Boolean(
    textoNormalizado.includes("recuperado dra malu") ||
    textoNormalizado.includes("recuperada dra malu") ||
    textoNormalizado.includes("dra malu recuperou") ||
    textoNormalizado.includes("chamado recuperado pela dra malu") ||
    textoNormalizado.includes("lead recuperada pela dra malu") ||
    textoNormalizado.includes("recuperado pela dra malu ia"),
  );
}

function getMensagemPreviewCard(conversa = {}, leadResumo = {}) {
  if (isRecuperadoDraMaluCard(conversa)) {
    return DRA_MALU_RECUPERADO_BADGE;
  }

  const texto =
    conversa.preview ||
    conversa.ultima_mensagem ||
    conversa.ultimaMensagem ||
    conversa.last_message ||
    conversa.mensagem ||
    leadResumo.preview ||
    "";

  const limpo = String(texto || "").trim();

  if (!limpo) return "";
  if (normalizar(limpo) === "conversa whatsapp") return "";

  return limpo;
}

function getTempoSemRespostaCard(conversa = {}, agora = Date.now()) {
  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  const sinalOperacional = getSinalPrecisaResponderCard(conversa);
  if (sinalOperacional === false) return null;

  const ultimaMensagem = getUltimaMensagemConversa(conversa, mensagens);
  const ultimaEhCliente = ultimaMensagem
    ? isMensagemCliente(ultimaMensagem)
    : false;
  const ultimaEhOperador = ultimaMensagem
    ? isMensagemOperador(ultimaMensagem)
    : false;
  const temNaoLida = Number(conversa.naoLidas || 0) > 0;

  if (ultimaEhOperador && !temNaoLida) return null;

  const status = normalizar(conversa.status || "");
  const estaNaFila =
    status.includes("aguardando") ||
    status.includes("fila") ||
    status.includes("pendente") ||
    status.includes("nova");

  // Uma automação genérica (ex.: lembrete de consulta) nunca cria espera por
  // resposta. Quando existe pendência real preservada pela API, o sinal
  // operacional acima é true e mantém o contato na fila.
  if (
    !ultimaEhCliente &&
    !temNaoLida &&
    !estaNaFila &&
    sinalOperacional !== true
  ) {
    return null;
  }

  const timestamp =
    parseTimestamp(
      conversa.ultima_mensagem_operacional_em ||
        conversa.ultimaMensagemOperacionalEm ||
        "",
    ) ||
    getMensagemTimestamp(ultimaMensagem) ||
    getConversaTimestamp(conversa) ||
    getTimestampFromHoraCurta(conversa.hora);

  if (!timestamp) return null;

  const diffMs = Math.max(0, Number(agora || Date.now()) - timestamp);

  if (!Number.isFinite(diffMs) || diffMs < 0) return null;

  return {
    ms: diffMs,
    label: formatarTempoEspera(diffMs, false),
    shortLabel: formatarTempoEspera(diffMs, true),
  };
}

function getPrioridadeOperacionalConversa(
  conversa = {},
  aba = "",
  agora = Date.now(),
) {
  if (aba === "fechados") return 0;

  if (isChamadoFechadoCard(conversa)) return 0;

  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  const esperaResposta = getTempoSemRespostaCard(conversa, agora);

  // A ordenação precisa seguir exatamente o mesmo critério visual do card.
  // Se o card mostra "Responder", ele deve ficar no topo.
  if (esperaResposta) return 1000;

  const ultimaOperacional = getUltimaMensagemConversa(conversa, mensagens);

  const naoLidas = Number(conversa.naoLidas || 0);
  if (naoLidas > 0) return 950;

  const status = normalizar(
    [
      conversa.status,
      conversa.status_chamado,
      conversa.chamado_status,
      conversa.aba_captazap,
    ]
      .filter(Boolean)
      .join(" "),
  );

  const estaNaFila = Boolean(
    conversa.em_fila ||
    conversa.fila_atendimento ||
    conversa.aguardando_atendimento ||
    conversa._fila_global ||
    conversa._fila_equipe ||
    status.includes("fila") ||
    status.includes("aguardando"),
  );

  if (estaNaFila) return 900;

  const followUpResumo = getFollowUpResumoOperacionalCard(conversa, agora);

  if (
    followUpResumo?.status === "pendente_vencido" ||
    followUpResumo?.status === "pendente"
  ) {
    return 800;
  }

  const followUpPendente = getTempoFollowUpCard(conversa, agora);
  if (followUpPendente?.ms > 0) return 800;

  if (
    followUpResumo?.status === "aguardando" ||
    followUpResumo?.status === "pendente_futuro"
  ) {
    return 600;
  }

  if (ultimaOperacional && isMensagemOperador(ultimaOperacional)) {
    return 300;
  }

  return 100;
}

function getEstadoAtendimentoVisualCard(
  conversa = {},
  {
    chamadoFechado = false,
    reaberturaResponder = false,
    agora = Date.now(),
  } = {},
) {
  if (chamadoFechado) {
    return {
      estado: "fechado",
      esperaResposta: null,
      ultimaMensagem: null,
    };
  }

  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  // Regra seletiva do selo:
  // - mensagem humana da clínica => Respondido;
  // - mensagem automática da Dra. Malu => Respondido;
  // - mensagem automática genérica (ex.: fora do horário) => ignorada;
  // - mensagem do cliente por último => Responder.
  //
  // Assim a resposta automática de horário não "finge" atendimento humano,
  // enquanto a Dra. Malu continua sendo reconhecida como agente que respondeu.
  const ultimaMensagemStatus = getUltimaMensagemValidaParaStatusCard(
    conversa,
    mensagens,
  );

  if (ultimaMensagemStatus?.tipo === "clinica") {
    return {
      estado: "respondido",
      esperaResposta: null,
      ultimaMensagem: ultimaMensagemStatus.mensagem,
    };
  }

  if (ultimaMensagemStatus?.tipo === "cliente") {
    return {
      estado: "responder",
      esperaResposta: getTempoSemRespostaCard(conversa, agora),
      ultimaMensagem: ultimaMensagemStatus.mensagem,
    };
  }

  // Fallback operacional mantido para payloads antigos que não trazem a
  // mensagem cronológica completa. Automação genérica não vale como resposta.
  const ultimaMensagem =
    getUltimaMensagemConversa(conversa, mensagens) ||
    getUltimaMensagemBrutaConversa(conversa);

  const ultimaEhOperador = ultimaMensagem
    ? isMensagemOperador(ultimaMensagem)
    : false;
  const ultimaEhCliente = ultimaMensagem
    ? isMensagemCliente(ultimaMensagem)
    : false;

  const naoLidas = Number(conversa.naoLidas || conversa.nao_lidas || 0) || 0;
  const esperaResposta = getTempoSemRespostaCard(conversa, agora);

  const statusOperacional = normalizar(
    [
      conversa.status_operacional,
      conversa.statusOperacional,
      conversa.badge_status,
      conversa.badgeStatus,
      conversa.acao_recomendada,
      conversa.acaoRecomendada,
      conversa.precisa_responder ? "responder" : "",
      conversa.precisaResponder ? "responder" : "",
    ]
      .filter(Boolean)
      .join(" "),
  );

  const direcaoFallback = normalizar(
    conversa.ultima_mensagem_direcao ||
      conversa.ultimaMensagemDirecao ||
      conversa.ultima_direcao ||
      conversa.ultimaDirecao ||
      conversa.last_direction ||
      conversa.direcao ||
      conversa.direction ||
      "",
  );

  const fallbackEhAutomacaoGenerica = Boolean(
    isMensagemAutomaticaSistema(conversa) &&
    !isMensagemDraMaluParaStatusCard(conversa),
  );
  const fallbackEhAutomacaoGenericaEnviada = Boolean(
    fallbackEhAutomacaoGenerica &&
    (direcaoFallback.includes("enviad") ||
      direcaoFallback.includes("saida") ||
      direcaoFallback.includes("outbound")),
  );

  // Regra visual oficial do CaptaZap:
  // cliente falou por último => Responder/verde;
  // operador humano ou Dra. Malu falou por último => Respondido/azul;
  // automação genérica não altera o estado operacional.
  if (ultimaEhOperador) {
    return {
      estado: "respondido",
      esperaResposta: null,
      ultimaMensagem,
    };
  }

  if (ultimaEhCliente) {
    return {
      estado: "responder",
      esperaResposta,
      ultimaMensagem,
    };
  }

  if (
    reaberturaResponder ||
    naoLidas > 0 ||
    (statusOperacional.includes("responder") &&
      !fallbackEhAutomacaoGenericaEnviada) ||
    Boolean(esperaResposta) ||
    direcaoFallback.includes("recebid") ||
    direcaoFallback.includes("entrada") ||
    direcaoFallback.includes("inbound")
  ) {
    return {
      estado: "responder",
      esperaResposta,
      ultimaMensagem,
    };
  }

  if (
    statusOperacional.includes("respondido") ||
    statusOperacional.includes("respondida") ||
    statusOperacional.includes("sem pendencia") ||
    (!fallbackEhAutomacaoGenerica &&
      (direcaoFallback.includes("enviad") ||
        direcaoFallback.includes("saida") ||
        direcaoFallback.includes("outbound")))
  ) {
    return {
      estado: "respondido",
      esperaResposta: null,
      ultimaMensagem,
    };
  }

  return {
    estado: "neutro",
    esperaResposta: null,
    ultimaMensagem,
  };
}

function ordenarMensagensStatusCard(conversa = {}, mensagens = []) {
  const lista = Array.isArray(mensagens) ? mensagens.filter(Boolean) : [];

  if (!lista.length && conversa && typeof conversa === "object") {
    const fallback = getUltimaMensagemBrutaConversa(conversa);
    return fallback ? [fallback] : [];
  }

  return [...lista].sort((a, b) => {
    const dataA = getMensagemTimestamp(a) || 0;
    const dataB = getMensagemTimestamp(b) || 0;

    if (dataA !== dataB) return dataB - dataA;

    const idA = Number(a?.id || a?.whatsapp_id || a?.message_id || 0);
    const idB = Number(b?.id || b?.whatsapp_id || b?.message_id || 0);
    return idB - idA;
  });
}

function isMensagemDraMaluParaStatusCard(msg = {}) {
  if (!msg || typeof msg !== "object") return false;

  const flagDireta = Boolean(
    msg.mensagem_dra_malu ||
    msg.mensagemDraMalu ||
    msg._mensagem_dra_malu ||
    msg._captazap_mensagem_dra_malu ||
    msg._captazap_dra_malu ||
    msg.dra_malu_atendimento_id ||
    msg.draMaluAtendimentoId,
  );

  if (flagDireta) return true;

  const marcadores = normalizar(
    [
      msg.origem_envio,
      msg.origemEnvio,
      msg.origem,
      msg.automacao_tipo,
      msg.automacaoTipo,
      msg.ultima_automacao_tipo,
      msg.ultimaAutomacaoTipo,
      msg.tipo_sistema,
      msg.tipoSistema,
      msg.canal,
      msg.autor,
      msg.de,
    ]
      .filter(Boolean)
      .join(" "),
  );

  return Boolean(
    marcadores.includes("dra malu") ||
    marcadores.includes("dra malu ia") ||
    marcadores.includes("dra_malu") ||
    marcadores.includes("dra_malu_ia"),
  );
}

function getUltimaMensagemValidaParaStatusCard(conversa = {}, mensagens = []) {
  const ordenadas = ordenarMensagensStatusCard(conversa, mensagens);

  for (const mensagem of ordenadas) {
    if (isMensagemCliente(mensagem)) {
      return { tipo: "cliente", mensagem };
    }

    const enviadaPelaClinica = isMensagemEnviadaPelaClinicaCard(mensagem);
    const ehDraMalu = isMensagemDraMaluParaStatusCard(mensagem);
    const ehAutomacaoGenerica =
      isMensagemAutomaticaSistema(mensagem) && !ehDraMalu;

    if (enviadaPelaClinica && ehDraMalu) {
      return { tipo: "clinica", origem: "dra_malu", mensagem };
    }

    if (ehAutomacaoGenerica) {
      // Ex.: aviso de atendimento fora do horário. Ignora e continua
      // procurando a última interação operacional real.
      continue;
    }

    if (isMensagemOperador(mensagem) || enviadaPelaClinica) {
      return { tipo: "clinica", origem: "operador", mensagem };
    }}

  return null;
}

function isMensagemEnviadaPelaClinicaCard(msg = {}) {
  const direcao = normalizar(
    msg.direcao || msg.direction || msg.tipo_direcao || msg.sentido || "",
  );
  const fromMe =
    msg.fromMe === true ||
    msg.from_me === true ||
    String(msg.fromMe || msg.from_me || "").toLowerCase() === "true";

  return Boolean(
    fromMe ||
    direcao.includes("enviad") ||
    direcao.includes("saida") ||
    direcao.includes("outbound"),
  );
}

function getUltimaMensagemConversa(conversa = {}, mensagens = []) {
  if (mensagens.length > 0) {
    const ordenadas = [...mensagens].sort((a, b) => {
      const dataA = getMensagemTimestamp(a) || 0;
      const dataB = getMensagemTimestamp(b) || 0;
      return dataB - dataA;
    });

    // Mensagem automática enviada pelo sistema não conta como resposta humana.
    // Para decidir se o captador respondeu ou se o cliente aguarda retorno,
    // usamos a última mensagem operacional real: cliente ou operador humano.
    return (
      ordenadas.find((mensagem) => !isMensagemAutomaticaSistema(mensagem)) ||
      null
    );
  }

  const direcao =
    conversa.ultima_mensagem_direcao ||
    conversa.ultimaMensagemDirecao ||
    conversa.ultima_direcao ||
    conversa.ultimaDirecao ||
    conversa.last_direction ||
    conversa.direcao ||
    conversa.direction ||
    conversa.sentido ||
    "";

  const autor =
    conversa.ultima_mensagem_autor ||
    conversa.ultimaMensagemAutor ||
    conversa.autor_ultima_mensagem ||
    conversa.autorUltimaMensagem ||
    conversa.autor ||
    conversa.de ||
    conversa.from ||
    conversa.remetente ||
    conversa.sender ||
    "";

  const texto =
    conversa.preview ||
    conversa.ultima_mensagem ||
    conversa.ultimaMensagem ||
    conversa.last_message ||
    conversa.mensagem ||
    conversa.texto ||
    "";

  if (!direcao && !autor && !texto) return null;

  return {
    ...conversa,
    direcao,
    autor,
    texto,
    datahora:
      conversa.ultimaMensagemEm ||
      conversa.ultima_mensagem_em ||
      conversa.last_message_at ||
      conversa.data_ultima_mensagem ||
      conversa.updated_at ||
      conversa.created_at ||
      conversa.datahora ||
      conversa.timestamp ||
      "",
  };
}

function getUltimaMensagemAutomaticaSistema(conversa = {}, mensagens = []) {
  const lista =
    Array.isArray(mensagens) && mensagens.length
      ? mensagens
      : conversa
        ? [conversa]
        : [];

  const ordenadas = [...lista].filter(Boolean).sort((a, b) => {
    const dataA = getMensagemTimestamp(a) || getConversaTimestamp(a) || 0;
    const dataB = getMensagemTimestamp(b) || getConversaTimestamp(b) || 0;
    return dataB - dataA;
  });

  const encontrada = ordenadas.find((mensagem) =>
    isMensagemAutomaticaSistema(mensagem),
  );

  if (encontrada) return encontrada;

  if (conversa && isMensagemAutomaticaSistema(conversa)) {
    return {
      ...conversa,
      direcao:
        conversa.direcao ||
        conversa.ultima_direcao ||
        conversa.ultimaDirecao ||
        conversa.last_direction ||
        "enviada",
      autor:
        conversa.autor ||
        conversa.autor_ultima_mensagem ||
        conversa.ultima_mensagem_autor ||
        conversa.ultimaMensagemAutor ||
        "sistema",
      texto:
        conversa.preview ||
        conversa.ultima_mensagem ||
        conversa.ultimaMensagem ||
        conversa.last_message ||
        conversa.mensagem ||
        "",
      datahora:
        conversa.ultimaMensagemEm ||
        conversa.ultima_mensagem_em ||
        conversa.last_message_at ||
        conversa.updated_at ||
        conversa.created_at ||
        conversa.datahora ||
        conversa.timestamp ||
        "",
    };
  }

  return null;
}

function isMensagemAutomaticaSistema(msg = {}) {
  const autor = normalizar(
    msg.de ||
      msg.autor ||
      msg.from ||
      msg.remetente ||
      msg.sender ||
      msg.tipo_autor ||
      msg.autor_ultima_mensagem ||
      msg.ultima_mensagem_autor ||
      "",
  );

  const origem = normalizar(
    msg.origem ||
      msg.origem_envio ||
      msg.tipo_envio ||
      msg.automacao_tipo ||
      msg.ultima_automacao_tipo ||
      msg.ultimaAutomacaoTipo ||
      "",
  );

  const automacaoFlag =
    msg.automacao === true ||
    msg.automacao === 1 ||
    String(msg.automacao || "").toLowerCase() === "true" ||
    Boolean(
      msg.automacao_tipo ||
      msg.ultima_automacao_tipo ||
      msg.ultimaAutomacaoTipo,
    );

  return Boolean(
    automacaoFlag ||
    autor.includes("sistema") ||
    autor.includes("bot") ||
    autor.includes("automatico") ||
    autor.includes("automático") ||
    autor.includes("automacao") ||
    autor.includes("automação") ||
    origem.includes("sistema") ||
    origem.includes("bot") ||
    origem.includes("automatico") ||
    origem.includes("automático") ||
    origem.includes("automacao") ||
    origem.includes("automação"),
  );
}

function isMensagemCliente(msg = {}) {
  const direcao = normalizar(
    msg.direcao || msg.direction || msg.tipo_direcao || msg.sentido || "",
  );
  const autor = normalizar(
    msg.de ||
      msg.autor ||
      msg.from ||
      msg.remetente ||
      msg.sender ||
      msg.tipo_autor ||
      "",
  );
  const fromMe =
    msg.fromMe === true ||
    msg.from_me === true ||
    String(msg.fromMe || msg.from_me || "").toLowerCase() === "true";

  if (fromMe) return false;

  return (
    direcao.includes("recebid") ||
    direcao.includes("entrada") ||
    direcao.includes("inbound") ||
    autor.includes("cliente") ||
    autor.includes("contato") ||
    autor.includes("lead")
  );
}

function isMensagemOperador(msg = {}) {
  if (isMensagemAutomaticaSistema(msg)) return false;

  const direcao = normalizar(
    msg.direcao || msg.direction || msg.tipo_direcao || msg.sentido || "",
  );
  const autor = normalizar(
    msg.de ||
      msg.autor ||
      msg.from ||
      msg.remetente ||
      msg.sender ||
      msg.tipo_autor ||
      "",
  );
  const fromMe =
    msg.fromMe === true ||
    msg.from_me === true ||
    String(msg.fromMe || msg.from_me || "").toLowerCase() === "true";

  return Boolean(
    fromMe ||
    direcao.includes("enviad") ||
    direcao.includes("saida") ||
    direcao.includes("outbound") ||
    autor.includes("voce") ||
    autor.includes("você") ||
    autor.includes("operador") ||
    autor.includes("atendente") ||
    autor.includes("captador") ||
    autor.includes("usuario") ||
    autor.includes("usuário"),
  );
}

function getMensagemTimestamp(msg = {}) {
  if (!msg) return 0;
  return parseTimestamp(
    msg.datahora ||
      msg.created_at ||
      msg.updated_at ||
      msg.enviada_em ||
      msg.recebida_em ||
      msg.timestamp ||
      msg.time ||
      msg.date ||
      "",
  );
}

function getConversaTimestamp(conversa = {}) {
  return parseTimestamp(
    conversa.ultimaMensagemEm ||
      conversa.ultima_mensagem_em ||
      conversa.last_message_at ||
      conversa.data_ultima_mensagem ||
      conversa.datahora ||
      conversa.timestamp ||
      conversa.updated_at ||
      conversa.created_at ||
      "",
  );
}

function parseTimestamp(value) {
  if (!value) return 0;

  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  if (typeof value === "number") {
    return value < 10000000000 ? value * 1000 : value;
  }

  const text = String(value || "").trim();
  if (!text) return 0;

  const mysqlDate = text.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
  );

  if (mysqlDate) {
    const parsedMysql = new Date(
      Number(mysqlDate[1]),
      Number(mysqlDate[2]) - 1,
      Number(mysqlDate[3]),
      Number(mysqlDate[4] || 0),
      Number(mysqlDate[5] || 0),
      Number(mysqlDate[6] || 0),
      0,
    ).getTime();

    if (!Number.isNaN(parsedMysql)) return parsedMysql;
  }

  const parsed = new Date(text).getTime();
  if (!Number.isNaN(parsed)) return parsed;

  return getTimestampFromHoraCurta(text);
}

function getTimestampFromHoraCurta(value) {
  const text = String(value || "").trim();
  const match = text.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return 0;

  const data = new Date();
  data.setHours(Number(match[1]), Number(match[2]), 0, 0);

  const agora = Date.now();
  if (data.getTime() > agora + 60000) data.setDate(data.getDate() - 1);

  return data.getTime();
}

function formatarTempoEspera(ms, short = false) {
  const totalMinutos = Math.max(0, Math.floor(ms / 60000));
  if (totalMinutos < 1) return short ? "agora" : "menos de 1 min";
  if (totalMinutos < 60)
    return short ? `${totalMinutos}m` : `${totalMinutos} min`;

  const horas = Math.floor(totalMinutos / 60);
  const minutos = totalMinutos % 60;
  if (horas < 24) {
    if (short) return minutos ? `${horas}h${minutos}m` : `${horas}h`;
    return minutos ? `${horas}h ${minutos}min` : `${horas}h`;
  }

  const dias = Math.floor(horas / 24);
  const horasRestantes = horas % 24;
  if (short) return horasRestantes ? `${dias}d${horasRestantes}h` : `${dias}d`;
  return horasRestantes ? `${dias}d ${horasRestantes}h` : `${dias}d`;
}

function getTempoFollowUpCard(conversa = {}, agora = Date.now()) {
  if (!conversa._follow_up_pendente) return null;

  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];
  const ultimaMensagem = getUltimaMensagemConversa(conversa, mensagens);

  if (ultimaMensagem && !isMensagemOperador(ultimaMensagem)) return null;

  const timestamp =
    getMensagemTimestamp(ultimaMensagem) ||
    getConversaTimestamp(conversa) ||
    getTimestampFromHoraCurta(conversa.hora);

  const diffMsFromTimestamp = timestamp
    ? Math.max(0, Number(agora || Date.now()) - timestamp)
    : null;
  const diffMsFromPayload =
    conversa._follow_up_ms != null ? Number(conversa._follow_up_ms) : null;
  const diffMs = Number.isFinite(diffMsFromPayload)
    ? diffMsFromPayload
    : diffMsFromTimestamp;

  if (!Number.isFinite(diffMs) || diffMs < 0) return null;

  const etapaLabel =
    conversa._follow_up_etapa_label ||
    conversa._follow_up_titulo ||
    conversa.status_followup ||
    "Follow-up";
  const perdidoSugerido = Boolean(conversa._follow_up_perdido_sugerido);

  return {
    ms: diffMs,
    label: conversa._follow_up_label || formatarTempoEspera(diffMs, false),
    shortLabel:
      conversa._follow_up_short_label ||
      (perdidoSugerido
        ? "Perdido sugerido"
        : etapaLabel || formatarTempoEspera(diffMs, true)),
    etapaLabel,
    tipoMensagem:
      conversa._follow_up_tipo_mensagem ||
      conversa._follow_up_mensagem_rapida_tipo ||
      "",
    perdidoSugerido,
    descricao:
      conversa._follow_up_descricao ||
      conversa.status_followup_detalhe ||
      `Cliente sem retorno há ${formatarTempoEspera(diffMs, false)}`,
  };
}

function deveAparecerEmMinhasCaptaZap(conversa = {}) {
  if (isDraMaluEmRecuperacaoCard(conversa)) return false;
  if (isRecuperadoDraMaluCard(conversa)) return true;

  const resumoFollowUp = getFollowUpResumoOperacionalCard(conversa, Date.now());
  const temFollowUpAtivo = Boolean(
    resumoFollowUp &&
    !["cancelado", "realizado"].includes(String(resumoFollowUp.status || "")),
  );

  // Follow-up cancelado/realizado é histórico e não deve tirar a conversa de Minhas.
  if (!temFollowUpAtivo) return true;

  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  const ultimaMensagem = getUltimaMensagemConversa(conversa, mensagens);

  // Follow-up só volta para Minhas quando o cliente respondeu.
  if (ultimaMensagem && isMensagemCliente(ultimaMensagem)) return true;

  // Também permite entrada por não lidas quando o payload não trouxe mensagens completas.
  if (Number(conversa.naoLidas || 0) > 0) return true;

  return false;
}

function isFollowUpVencidoVisivel(conversa = {}, agora = Date.now()) {
  if (!conversa) return false;
  if (isDraMaluEmRecuperacaoCard(conversa)) return true;

  const resumo = getFollowUpResumoOperacionalCard(conversa, agora);
  if (!resumo) return false;

  return Boolean(
    resumo.status === "pendente_vencido" || resumo.status === "pendente",
  );
}
function getAtrasoFollowUpCard(conversa = {}, agora = Date.now()) {
  const liberadoEm = parseTimestamp(
    conversa._follow_up_liberado_em ||
      conversa.followup_liberado_em ||
      conversa.agendado_para ||
      conversa.agendadoPara ||
      conversa.janela_inicio ||
      conversa.proxima_execucao_em ||
      conversa.proximaExecucaoEm ||
      "",
  );

  if (!liberadoEm) {
    const msPayload = Number(conversa._follow_up_ms || 0);
    return {
      atrasado: msPayload > 0,
      ms: Math.max(0, msPayload),
      label:
        msPayload > 0
          ? `Vencido há ${formatarTempoEspera(msPayload, false)}`
          : "Pendente",
    };
  }

  const diffMs = Number(agora || Date.now()) - liberadoEm;

  return {
    atrasado: diffMs >= 0,
    ms: Math.abs(diffMs),
    label:
      diffMs >= 0
        ? `Vencido há ${formatarTempoEspera(diffMs, false)}`
        : `Vence em ${formatarTempoEspera(Math.abs(diffMs), false)}`,
  };
}

function formatarDataFollowUpCard(value) {
  const timestamp = parseTimestamp(value);
  if (!timestamp) return "";

  const data = new Date(timestamp);
  if (Number.isNaN(data.getTime())) return "";

  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatarTelefoneCard(value = "") {
  const digits = normalizarTelefone(value);

  if (digits.length >= 11) {
    const local = digits.slice(-11);
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }

  if (digits.length >= 10) {
    const local = digits.slice(-10);
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  }

  return String(value || "").trim();
}

function limitarTextoCard(value = "", limite = 120) {
  const texto = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!texto) return "";
  if (texto.length <= limite) return texto;
  return `${texto.slice(0, limite - 1).trim()}…`;
}

function getTipoAcaoFollowUpCard(conversa = {}) {
  const canal = normalizar(
    conversa._follow_up_canal ||
      conversa.canal_followup ||
      conversa.canal ||
      "",
  );

  if (canal.includes("ligacao") || canal.includes("telefon")) return "Ligação";
  if (canal.includes("audio")) return "Áudio";
  if (canal.includes("perdido")) return "Avaliar perdido";

  return "Mensagem";
}

function getOrdemFollowUpCard(conversa = {}) {
  const ordem = Number(
    conversa._follow_up_ordem ||
      conversa.followup_ordem ||
      conversa.ordem ||
      conversa.etapa ||
      0,
  );

  return Number.isFinite(ordem) && ordem > 0 ? ordem : null;
}

function EmptyList({ message }) {
  return (
    <div className="flex h-full items-center justify-center px-4">
      <div className="rounded-[26px] border border-slate-300 bg-white p-6 text-center shadow-sm">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 ring-1 ring-blue-200">
          <FaInbox size={22} />
        </div>
        <div className="text-sm font-bold text-slate-700">Nada por aqui</div>
        <p className="mt-1 text-xs leading-5 text-slate-400">{message}</p>
      </div>
    </div>
  );
}

function CanalIcon({ canal }) {
  const normalized = String(canal || "").toLowerCase();
  if (normalized.includes("whatsapp"))
    return <FaWhatsapp className="text-[#25D366]" size={14} />;
  if (normalized.includes("instagram"))
    return <FaInstagram className="text-[#C13584]" size={14} />;
  if (normalized.includes("facebook") || normalized.includes("messenger")) {
    return <FaFacebookMessenger className="text-[#1877F2]" size={14} />;
  }
  if (normalized.includes("telegram"))
    return <FaTelegramPlane className="text-[#229ED9]" size={14} />;
  return <FaInbox className="text-slate-500" size={13} />;
}

function getCanalStyle(canal) {
  const normalized = String(canal || "").toLowerCase();

  if (normalized.includes("whatsapp")) {
    return {
      accent: "bg-[#25D366]",
      iconBg: "bg-emerald-50",
      avatarRing: "ring-emerald-300",
      avatarBg: "bg-emerald-50 text-[#128C7E] ring-emerald-300",
      statusSelected: "border-emerald-200 bg-emerald-50 text-[#128C7E]",
    };
  }

  if (normalized.includes("instagram")) {
    return {
      accent: "bg-gradient-to-b from-[#833AB4] via-[#E1306C] to-[#FCAF45]",
      iconBg: "bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50",
      avatarRing: "ring-pink-300",
      avatarBg:
        "bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 text-[#C13584] ring-pink-300",
      statusSelected:
        "border-pink-200 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 text-[#C13584]",
    };
  }

  if (normalized.includes("facebook") || normalized.includes("messenger")) {
    return {
      accent: "bg-[#1877F2]",
      iconBg: "bg-blue-50",
      avatarRing: "ring-blue-300",
      avatarBg: "bg-blue-50 text-[#1877F2] ring-blue-300",
      statusSelected: "border-blue-200 bg-blue-50 text-[#1877F2]",
    };
  }

  if (normalized.includes("telegram")) {
    return {
      accent: "bg-[#229ED9]",
      iconBg: "bg-sky-50",
      avatarRing: "ring-sky-300",
      avatarBg: "bg-sky-50 text-[#229ED9] ring-sky-300",
      statusSelected: "border-sky-200 bg-sky-50 text-[#229ED9]",
    };
  }

  return {
    accent: "bg-slate-400",
    iconBg: "bg-slate-50",
    avatarRing: "ring-slate-300",
    avatarBg: "bg-slate-100 text-slate-500 ring-slate-300",
    statusSelected: "border-blue-200 bg-blue-50 text-blue-700",
  };
}

function getLeadsVinculadasCard(conversa = {}) {
  const candidatos = [
    conversa?.leads_vinculadas,
    conversa?.leadsVinculadas,
    conversa?.leads_relacionadas,
    conversa?.leadsRelacionadas,
    conversa?.telefone_leads,
    conversa?.telefoneLeads,
  ];

  const listaBruta = [];

  for (const candidato of candidatos) {
    if (Array.isArray(candidato) && candidato.length) {
      listaBruta.push(...candidato.filter(Boolean));
    }
  }

  if (conversa?.lead) {
    listaBruta.push(conversa.lead);
  }

  const listaOrdenada = listaBruta
    .filter((lead) => lead && typeof lead === "object")
    .map((lead, index) => ({ lead, index }))
    .sort((a, b) => {
      const aTemId = getLeadIdGenericoCard(a.lead) ? 1 : 0;
      const bTemId = getLeadIdGenericoCard(b.lead) ? 1 : 0;
      if (aTemId !== bTemId) return bTemId - aTemId;
      return a.index - b.index;
    });

  const seenIds = new Set();
  const seenPhones = new Set();
  const seenNames = new Set();
  const resultado = [];

  for (const { lead } of listaOrdenada) {
    const leadId = getLeadIdGenericoCard(lead);
    const telefone = normalizarTelefone(
      lead?.telefone1 ||
        lead?.telefone2 ||
        lead?.telefone ||
        lead?.celular ||
        lead?.whatsapp ||
        lead?.numero ||
        "",
    );
    const nome = normalizar(getNomeLeadVinculadaCard(lead));

    if (leadId) {
      const key = `lead:${leadId}`;
      if (seenIds.has(key)) continue;

      // Proteção contra o mesmo paciente chegando duas vezes: uma vez como
      // lead real e outra vez como contato/objeto sem id. O card deve contar
      // paciente por lead distinta, não por chamado ou por variação do objeto.
      seenIds.add(key);
      if (telefone) seenPhones.add(telefone);
      if (nome) seenNames.add(nome);
      resultado.push(lead);
      continue;
    }

    // Sem id confiável, este item só entra se realmente não parecer ser a
    // mesma pessoa já adicionada por id. Isso evita o selo falso "2 pacientes".
    if (telefone && seenPhones.has(telefone)) continue;
    if (nome && seenNames.has(nome)) continue;

    const fallbackKey = telefone || nome;
    if (!fallbackKey) continue;

    if (telefone) seenPhones.add(telefone);
    if (nome) seenNames.add(nome);
    resultado.push(lead);
  }

  return resultado;
}

function getLeadIdGenericoCard(lead = {}) {
  const bruto =
    lead?.id ?? lead?.lead_id ?? lead?.leadId ?? lead?.id_lead ?? null;

  if (bruto === null || bruto === undefined || bruto === "") return null;

  const texto = String(bruto).trim();
  const match = texto.match(/(?:^|[^0-9])(\d+)(?:$|[^0-9])/);
  const somenteDigitos = /^\d+$/.test(texto) ? texto : match?.[1] || "";

  if (!somenteDigitos) return texto;

  const numero = Number(somenteDigitos);
  return Number.isFinite(numero) && numero > 0 ? String(numero) : null;
}

function getLeadContextoIdCard(conversa = {}) {
  return (
    conversa?.lead_contexto_id ||
    conversa?.leadContextoId ||
    conversa?.lead_contexto?.id ||
    conversa?.leadContexto?.id ||
    null
  );
}

function getLeadPrincipalConversaCard(conversa = {}) {
  const leads = getLeadsVinculadasCard(conversa);
  const contextoId = getLeadContextoIdCard(conversa);

  if (contextoId && leads.length) {
    const porContexto = leads.find(
      (lead) =>
        String(getLeadIdGenericoCard(lead) || "") === String(contextoId),
    );
    if (porContexto) return porContexto;
  }

  const leadDireta = conversa?.lead || null;
  const leadDiretaId = getLeadIdGenericoCard(leadDireta);

  if (leadDireta && leadDiretaId && leads.length) {
    const porLeadDireta = leads.find(
      (lead) =>
        String(getLeadIdGenericoCard(lead) || "") === String(leadDiretaId),
    );
    if (porLeadDireta) return porLeadDireta;
  }

  if (leadDireta) return leadDireta;

  const leadId =
    conversa?.lead_id || conversa?.leadId || conversa?.id_lead || null;
  if (leadId && leads.length) {
    const porLeadId = leads.find(
      (lead) => String(getLeadIdGenericoCard(lead) || "") === String(leadId),
    );
    if (porLeadId) return porLeadId;
  }

  return leads[0] || {};
}

function getNomeLeadVinculadaCard(lead = {}) {
  return getPrimeiroTexto([
    lead?.nome_lead,
    lead?.nomeLead,
    lead?.nome,
    lead?.nome_cliente,
    lead?.paciente,
    lead?.telefone1,
    lead?.telefone,
    lead?.celular,
    lead?.whatsapp,
    lead?.numero,
  ]);
}

function getLeadResumo(conversa, usuarios = []) {
  const lead = getLeadPrincipalConversaCard(conversa) || conversa?.lead || {};

  const status = isChamadoFechadoCard(conversa)
    ? "Fechado"
    : conversa?.status === "lead_reativavel"
      ? "Reativável"
      : limparStatusNegociacao(
          lead.status_negociacao ||
            lead.status_agendamento ||
            conversa.status_negociacao ||
            conversa.status_agendamento ||
            conversa.status,
        );

  const valor =
    lead.total_oportunidade ??
    lead.valor_oportunidade ??
    lead.valor_oportunidades ??
    lead.valor_negociacao ??
    lead.total_orcamento ??
    lead.valor_orcamento ??
    lead.total_contratado ??
    lead.valor_contratado ??
    lead.valor_fechado ??
    lead.valor_fechamento ??
    lead.total_fechado ??
    lead.total_fechamento ??
    lead.valor_total ??
    conversa.total_oportunidade ??
    conversa.valor_oportunidade ??
    conversa.valor_oportunidades ??
    conversa.valor_negociacao ??
    conversa.total_orcamento ??
    conversa.valor_orcamento ??
    conversa.total_contratado ??
    conversa.valor_contratado ??
    conversa.valor_fechado ??
    conversa.valor_fechamento ??
    conversa.total_fechado ??
    conversa.total_fechamento ??
    conversa.valor_total ??
    conversa.valor ??
    null;

  const captadorLead = getCaptadorLeadConversa(conversa, lead, usuarios);
  const responsavelChamado = getResponsavelChamadoConversa(
    conversa,
    lead,
    usuarios,
  );

  const fonte = getFonteOuCampanhaCard(conversa, { lead });

  return {
    status,
    valor,
    dono: captadorLead,
    captadorLead,
    responsavelChamado,
    fonte,
    lead,
    preview: getPreviewOperacional(conversa, lead),
  };
}

function getCaptadorLeadConversa(conversa = {}, lead = {}, usuarios = []) {
  const captadorTexto = getPrimeiroTexto([
    lead.usuario_criador_nome,
    lead.captador_nome,
    lead.dono_nome,
    lead.owner_name,
    lead.ownerName,
    lead.usuario_nome,
    lead.nome_usuario,
    lead.vendedor_nome,
    lead.consultor_nome,
    lead.atendente_nome,
    getNomePessoa(lead.usuario_criador),
    getNomePessoa(lead.captador),
    getNomePessoa(lead.dono),
    getNomePessoa(lead.owner),
    getNomePessoa(lead.usuario),
    getNomePessoa(lead.user),
    conversa.lead_captador_nome,
    conversa.lead_usuario_criador_nome,
    conversa.lead_dono_nome,
    conversa.captador_lead_nome,
    conversa.dono_lead_nome,
    conversa.crm_captador_nome,
    conversa.crm_dono_nome,
  ]);

  if (captadorTexto) return captadorTexto;

  const captadorId = getPrimeiroTexto([
    lead.usuario_criador_id,
    lead.captador_id,
    lead.dono_id,
    lead.owner_id,
    lead.user_id,
    lead.id_usuario,
    conversa.lead_usuario_criador_id,
    conversa.lead_captador_id,
    conversa.captador_lead_id,
    conversa.crm_captador_id,
    conversa.dono_lead_id,
  ]);

  const nomePorId = getNomeUsuarioPorId(captadorId, usuarios);
  if (nomePorId) return nomePorId;
  if (captadorId) return "Captador vinculado";

  return "";
}

function getResponsavelChamadoConversa(
  conversa = {},
  lead = {},
  usuarios = [],
) {
  const responsavelTexto = getPrimeiroTexto([
    conversa.responsavel_nome,
    conversa.usuario_responsavel_nome,
    conversa.nome_responsavel,
    conversa.atendente_nome,
    conversa.operador_nome,
    conversa.captador_nome,
    conversa.usuario_nome,
    conversa.nome_usuario,
    getNomePessoa(conversa.responsavel),
    getNomePessoa(conversa.usuario_responsavel),
    getNomePessoa(conversa.usuarioResponsavel),
    getNomePessoa(conversa.atendente),
    getNomePessoa(conversa.operador),
    getNomePessoa(conversa.captador),
    getNomePessoa(conversa.usuario),
    getNomePessoa(conversa.user),
  ]);

  if (responsavelTexto) return responsavelTexto;

  const responsavelId = getPrimeiroTexto([
    conversa.responsavel_id,
    conversa.usuario_responsavel_id,
    conversa.atendente_id,
    conversa.operador_id,
    conversa.captador_id,
    conversa.user_id,
    conversa.id_usuario,
  ]);

  const nomePorId = getNomeUsuarioPorId(responsavelId, usuarios);
  if (nomePorId) return nomePorId;
  if (responsavelId) return "Responsável vinculado";

  return "";
}

function getDestaquePacienteCard(conversa = {}, leadResumo = {}) {
  const lead = getLeadPrincipalConversaCard(conversa) || conversa?.lead || {};
  const leadId = getPrimeiroTexto([
    conversa.lead_id,
    conversa.leadId,
    conversa.id_lead,
    lead.id,
    lead.lead_id,
    lead.id_lead,
  ]);

  const statusAgendamento = normalizar(
    lead.status_agendamento ||
      conversa.status_agendamento ||
      conversa.ultimo_agendamento?.status_agendamento ||
      conversa._ultimo_agendamento?.status_agendamento ||
      lead.ultimo_agendamento?.status_agendamento ||
      lead._ultimo_agendamento?.status_agendamento ||
      "",
  );

  const statusNegociacao = normalizar(
    lead.status_negociacao ||
      conversa.status_negociacao ||
      leadResumo.status ||
      conversa.status ||
      "",
  );

  const temComparecimento =
    statusAgendamento.includes("compareceu") ||
    statusAgendamento.includes("atendido") ||
    statusAgendamento.includes("realizado") ||
    statusAgendamento.includes("finalizado");

  const parecePaciente =
    temComparecimento ||
    statusNegociacao.includes("paciente") ||
    statusNegociacao.includes("tratamento") ||
    statusNegociacao.includes("cliente") ||
    statusNegociacao.includes("compareceu") ||statusNegociacao.includes("fechou") ||
    statusNegociacao.includes("venda") ||
    statusNegociacao.includes("contrat") ||
    Boolean(
      lead.paciente_id ||
      lead.id_paciente ||
      conversa.paciente_id ||
      conversa.id_paciente,
    );

  if (parecePaciente) {
    return {
      nivel: "paciente",
      label: "Paciente",
      chip: "Paciente",
      title: "Contato já vinculado a paciente/atendimento da clínica",
    };
  }

  if (leadId) {
    return {
      nivel: "lead",
      label: "Lead CRM",
      chip: "Lead CRM",
      title: "Contato já existe como lead no CRM",
    };
  }

  return { nivel: "", label: "", chip: "", title: "" };
}

function getPrimeiroTexto(values = []) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (!text) continue;
    if (text === "0") continue;
    if (text.toLowerCase() === "null") continue;
    if (text.toLowerCase() === "undefined") continue;
    return text;
  }
  return "";
}

function getNomePessoa(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value !== "object") return "";

  return getPrimeiroTexto([
    value.nome,
    value.name,
    value.nome_usuario,
    value.usuario,
    value.apelido,
    value.email,
  ]);
}

function getNomeUsuarioPorId(id, usuarios = []) {
  if (!id || !Array.isArray(usuarios)) return "";

  const usuario = usuarios.find((item) => {
    const idsPossiveis = [
      item?.id,
      item?.usuario_id,
      item?.id_usuario,
      item?.user_id,
      item?.value,
    ];
    return idsPossiveis.some((value) => String(value || "") === String(id));
  });

  return usuario ? getNomePessoa(usuario) : "";
}

function getPreviewOperacional(conversa, lead) {
  if (isChamadoFechadoCard(conversa)) return "Atendimento encerrado";
  if (conversa?.lead_reativavel || lead?._captaZap_reativavel)
    return "Lead antiga";
  if (conversa?.sem_conversa) return "Sem conversa";
  if (conversa?.naoLidas > 0) return `${conversa.naoLidas} nova(s)`;
  return "";
}

function limparStatusNegociacao(status) {
  if (!status) return "Sem status";
  return String(status)
    .replace(/^negocia[cç][aã]o\s*:\s*/i, "")
    .replace(/^status\s*:\s*/i, "")
    .replace(/^lead\s*:\s*/i, "")
    .replaceAll("_", " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getStatusStyle(status) {
  const normalized = normalizar(status);

  if (normalized.includes("reativ")) {
    return {
      icon: RotateCcw,
      accent: "bg-orange-500",
      accentSoft: "bg-orange-300",
      badge: "border-orange-200 bg-orange-50 text-orange-700",
    };
  }

  if (normalized.includes("prospect")) {
    return {
      icon: Radar,
      accent: "bg-blue-600",
      accentSoft: "bg-blue-300",
      badge: "border-blue-200 bg-blue-50 text-blue-700",
    };
  }

  if (normalized.includes("negoci")) {
    return {
      icon: Handshake,
      accent: "bg-violet-600",
      accentSoft: "bg-violet-300",
      badge: "border-violet-200 bg-violet-50 text-violet-700",
    };
  }

  if (normalized.includes("agend")) {
    return {
      icon: CalendarCheck,
      accent: "bg-emerald-600",
      accentSoft: "bg-emerald-300",
      badge: "border-emerald-200 bg-emerald-50 text-emerald-700",
    };
  }

  if (normalized.includes("comparec")) {
    return {
      icon: BadgeCheck,
      accent: "bg-green-600",
      accentSoft: "bg-green-300",
      badge: "border-green-200 bg-green-50 text-green-700",
    };
  }

  if (normalized.includes("falt")) {
    return {
      icon: UserX,
      accent: "bg-rose-600",
      accentSoft: "bg-rose-300",
      badge: "border-rose-200 bg-rose-50 text-rose-700",
    };
  }

  if (
    normalized.includes("fech") ||
    normalized.includes("ganh") ||
    normalized.includes("contrat")
  ) {
    return {
      icon: Trophy,
      accent: "bg-slate-600",
      accentSoft: "bg-slate-300",
      badge: "border-slate-200 bg-slate-100 text-slate-700",
    };
  }

  if (
    normalized.includes("perdid") ||
    normalized.includes("cancel") ||
    normalized.includes("descart") ||
    normalized.includes("inativ") ||
    normalized.includes("desqual")
  ) {
    return {
      icon: Ban,
      accent: "bg-slate-500",
      accentSoft: "bg-slate-300",
      badge: "border-slate-200 bg-slate-100 text-slate-600",
    };
  }

  if (normalized.includes("novo") || normalized.includes("lead crm")) {
    return {
      icon: Sparkles,
      accent: "bg-cyan-600",
      accentSoft: "bg-cyan-300",
      badge: "border-cyan-200 bg-cyan-50 text-cyan-700",
    };
  }

  return {
    icon: Tag,
    accent: "bg-slate-500",
    accentSoft: "bg-slate-300",
    badge: "border-slate-200 bg-slate-50 text-slate-600",
  };
}

function formatMoney(value) {
  const number = normalizarValorMonetarioCard(value);

  if (!Number.isFinite(number) || number <= 0) return "";

  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function normalizarValorMonetarioCard(value) {
  if (value == null || value === "") return 0;

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0;

    if (Number.isInteger(value) && Math.abs(value) >= 100000) {
      return value / 100;
    }

    return value;
  }

  const raw = String(value)
    .replace(/\s|R\$\s?/g, "")
    .trim();

  if (!raw) return 0;

  if (/^-?\d{1,3}(\.\d{3})+,\d{1,2}$/.test(raw)) {
    return Number(raw.replace(/\./g, "").replace(",", "."));
  }

  if (/^-?\d+,\d{1,2}$/.test(raw)) {
    return Number(raw.replace(",", "."));
  }

  if (/^-?\d+\.\d{1,2}$/.test(raw)) {
    return Number(raw);
  }

  if (/^-?\d+$/.test(raw)) {
    const inteiro = Number(raw);
    if (!Number.isFinite(inteiro)) return 0;
    return Math.abs(inteiro) >= 100000 ? inteiro / 100 : inteiro;
  }

  const clean = raw.replace(/[^\d,.-]/g, "");

  if (clean.includes(",") && clean.includes(".")) {
    return Number(clean.replace(/\./g, "").replace(",", "."));
  }

  if (clean.includes(",")) {
    return Number(clean.replace(",", "."));
  }

  const parsed = Number(clean.replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function conversaBateBuscaGlobal(conversa, termo) {
  const lead = getLeadPrincipalConversaCard(conversa) || conversa?.lead || {};
  const leadsVinculadas = getLeadsVinculadasCard(conversa);
  const telefoneNormalizado = normalizarTelefone(termo);

  const camposLeadsVinculadas = leadsVinculadas.flatMap((item) => [
    item?.nome_lead,
    item?.nomeLead,
    item?.nome,
    item?.nome_cliente,
    item?.paciente,
    item?.telefone1,
    item?.telefone2,
    item?.telefone,
    item?.celular,
    item?.whatsapp,
    item?.numero,
    item?.status_negociacao,
    item?.status_agendamento,
    item?.profissional_nome,
    item?.nome_profissional,
    item?.dentista_nome,
    item?.nome_dentista,
    item?.procedimento_nome,
    item?.nome_procedimento,
  ]);

  const camposTexto = [
    conversa?.nome,
    conversa?.nome_whatsapp,
    conversa?.nomeWhatsapp,
    conversa?.contato_nome_whatsapp,
    conversa?.contatoNomeWhatsapp,
    conversa?.nome_lead,
    conversa?.nomeLead,
    conversa?.nome_cliente,
    conversa?.paciente,
    conversa?.id,
    conversa?.lead_id,
    conversa?.leadId,
    conversa?.id_lead,
    conversa?.numero,
    conversa?.telefone,
    conversa?.telefone1,
    conversa?.telefone2,
    conversa?.celular,
    conversa?.whatsapp,
    conversa?.telefone_normalizado,
    conversa?.preview,
    conversa?.status,
    conversa?.status_negociacao,
    conversa?.status_agendamento,
    conversa?.crc,
    conversa?.cpf,
    conversa?.documento,
    conversa?.fonte_lead,
    conversa?.potencial,
    conversa?.status_chamado,
    conversa?.motivo_fechamento,
    conversa?.origem,
    conversa?.empresa_nome,
    conversa?.empresaNome,
    conversa?.captador_nome,
    conversa?.responsavel_nome,
    conversa?.usuario_responsavel_nome,
    conversa?.nome_responsavel,
    conversa?.lead_captador_nome,
    conversa?.dono_lead_nome,
    conversa?.crm_captador_nome,
    conversa?.tags?.join(" "),
    lead?.nome_lead,
    lead?.nome,
    lead?.telefone1,
    lead?.telefone,
    lead?.celular,
    lead?.whatsapp,
    lead?.numero,
    lead?.status_negociacao,
    lead?.status_agendamento,
    lead?.usuario_criador_nome,
    lead?.captador_nome,
    lead?.responsavel_nome,
    lead?.usuario_responsavel_nome,
    lead?.nome_responsavel,
    lead?.dono_nome,
    lead?.owner_name,
    lead?.ownerName,
    lead?.usuario_nome,
    lead?.nome_usuario,
    lead?.vendedor_nome,
    lead?.consultor_nome,
    lead?.atendente_nome,
    getNomePessoa(conversa?.captador),
    getNomePessoa(conversa?.responsavel),
    getNomePessoa(conversa?.usuario_responsavel),
    getNomePessoa(conversa?.dono),
    getNomePessoa(conversa?.owner),
    getNomePessoa(lead?.usuario_criador),
    getNomePessoa(lead?.captador),
    getNomePessoa(lead?.responsavel),
    getNomePessoa(lead?.usuario_responsavel),
    getNomePessoa(lead?.dono),
    getNomePessoa(lead?.owner),
    lead?.nome_fonte,
    lead?.fonte,
    lead?.nome_campanha,
    lead?.nome_procedimento,
    lead?.nome_regiao,
    lead?.nome_dentista,
    lead?.profissional_nome,
    lead?.nome_profissional,
    lead?.procedimento_nome,
    lead?.nome_procedimento,
    ...camposLeadsVinculadas,
  ];

  const textoUnificado = camposTexto
    .map((campo) => normalizar(campo))
    .filter(Boolean)
    .join(" ");

  if (textoBateTermoBuscaCaptaZap(textoUnificado, termo)) return true;
  if (!telefoneNormalizado) return false;

  const telefones = [
    conversa?.numero,
    conversa?.telefone,
    conversa?.telefone1,
    conversa?.telefone2,
    conversa?.celular,
    conversa?.whatsapp,
    conversa?.telefone_normalizado,
    lead?.telefone1,
    lead?.telefone2,
    lead?.telefone,
    lead?.celular,
    lead?.whatsapp,
    lead?.numero,
    ...leadsVinculadas.flatMap((item) => [
      item?.telefone1,
      item?.telefone2,
      item?.telefone,
      item?.celular,
      item?.whatsapp,
      item?.numero,
    ]),
  ];

  return telefones.some((telefone) =>
    telefoneBateBusca(telefone, telefoneNormalizado),
  );
}

function textoBateTermoBuscaCaptaZap(texto, termo) {
  const alvo = normalizar(texto);
  const busca = normalizar(termo);

  if (!alvo || !busca) return false;
  if (alvo.includes(busca)) return true;

  const tokens = busca.split(" ").filter((parte) => parte.length >= 2);
  if (tokens.length <= 1) return false;

  return tokens.every((token) => alvo.includes(token));
}

function telefoneBateBusca(telefone, buscaDigits) {
  const busca = normalizarTelefone(buscaDigits);
  const tel = normalizarTelefone(telefone);

  if (!busca || !tel) return false;

  const variantesTelefone = montarVariantesTelefoneBusca(tel);
  const variantesBusca = montarVariantesTelefoneBusca(busca);

  return variantesTelefone.some((telefoneVariante) =>
    variantesBusca.some(
      (buscaVariante) =>
        telefoneVariante.includes(buscaVariante) ||
        buscaVariante.includes(telefoneVariante),
    ),
  );
}

function montarVariantesTelefoneBusca(valor) {
  const digits = normalizarTelefone(valor);
  if (!digits) return [];

  const variantes = new Set([digits]);
  const semDDI = digits.startsWith("55") ? digits.slice(2) : digits;

  if (semDDI) {
    variantes.add(semDDI);
    variantes.add(`55${semDDI}`);
  }

  if (semDDI.length === 10) {
    const comNono = `${semDDI.slice(0, 2)}9${semDDI.slice(2)}`;
    variantes.add(comNono);
    variantes.add(`55${comNono}`);
  }

  if (semDDI.length === 11 && semDDI[2] === "9") {
    const semNono = `${semDDI.slice(0, 2)}${semDDI.slice(3)}`;
    variantes.add(semNono);
    variantes.add(`55${semNono}`);
  }

  return [...variantes].filter(Boolean);
}

function dedupeConversasLocal(lista = []) {
  const map = new Map();

  for (const item of Array.isArray(lista) ? lista : []) {
    if (!item || typeof item !== "object") continue;

    const key = getConversationKey(item);
    if (!key) continue;

    const atual = map.get(key);
    map.set(key, atual ? mesclarContatoBuscaCaptaZap(atual, item) : item);
  }

  return [...map.values()];
}

function getTabCount(key, listas = {}) {
  const minhas = Array.isArray(listas.minhasConversas)
    ? listas.minhasConversas.length
    : 0;
  const fila = Array.isArray(listas.filaDeEspera)
    ? listas.filaDeEspera.length
    : 0;
  const followUp = Array.isArray(listas.followUpConversas)
    ? listas.followUpConversas.length
    : 0;
  const contatos = Array.isArray(listas.contatos) ? listas.contatos.length : 0;
  const fechados = Array.isArray(listas.fechadosConversas)
    ? listas.fechadosConversas.length
    : 0;

  if (key === "minhas") return minhas;
  if (key === "fila") return fila;
  if (key === "followup") return followUp;
  if (key === "contatos") return contatos;
  if (key === "fechados") return Number(listas.totalFechados || 0) || fechados;
  return 0;
}

function getConversationKey(conversa) {
  const empresaId =
    conversa?.empresa_id || conversa?.empresaId || "sem-empresa";
  const telefone = normalizarTelefone(conversa?.numero || conversa?.telefone);
  const telefoneKey = telefone ? telefone.slice(-11) : "";
  const instancia = String(
    conversa?.zapi_instance_id ||
      conversa?.zapiInstanceId ||
      conversa?.conexao_instance_id ||
      conversa?.conexaoInstanceId ||
      conversa?.instance_id ||
      conversa?.instanceId ||
      conversa?.chamada?.zapi_instance_id ||
      conversa?.chamado?.zapi_instance_id ||
      "",
  ).trim();
  const leadId = conversa?.lead_id || conversa?.leadId || conversa?.lead?.id;
  const conversaId =
    conversa?.chamado_id ||
    conversa?.id_chamado ||
    conversa?.ticket_id ||
    conversa?.conversa_id ||
    conversa?.conversaId ||
    conversa?.id ||
    "";

  // No CaptaZap, a identidade operacional da conversa é o telefone.
  // Isso evita duplicar/reordenar errado quando o contato é convertido em lead.
  if (instancia && telefoneKey) {
    return `${empresaId}-inst-${instancia}-tel-${telefoneKey}`;
  }
  if (telefoneKey) return `${empresaId}-tel-${telefoneKey}`;
  if (conversaId) {
    return instancia
      ? `${empresaId}-inst-${instancia}-chamado-${conversaId}`
      : `${empresaId}-chamado-${conversaId}`;
  }
  if (leadId) {
    return instancia
      ? `${empresaId}-inst-${instancia}-lead-${leadId}`
      : `${empresaId}-lead-${leadId}`;
  }

  return `${empresaId}-sem-chave`;
}

function isSameConversation(a, b) {
  if (!a || !b) return false;

  const empresaA = String(a.empresa_id || a.empresaId || "");
  const empresaB = String(b.empresa_id || b.empresaId || "");
  if (empresaA && empresaB && empresaA !== empresaB) return false;

  const instanciaA = String(
    a.zapi_instance_id ||
      a.zapiInstanceId ||
      a.conexao_instance_id ||
      a.conexaoInstanceId ||
      a.instance_id ||
      a.instanceId ||
      a.chamada?.zapi_instance_id ||
      a.chamado?.zapi_instance_id ||
      "",
  ).trim();
  const instanciaB = String(
    b.zapi_instance_id ||
      b.zapiInstanceId ||
      b.conexao_instance_id ||
      b.conexaoInstanceId ||
      b.instance_id ||
      b.instanceId ||
      b.chamada?.zapi_instance_id ||
      b.chamado?.zapi_instance_id ||
      "",
  ).trim();
  if (instanciaA && instanciaB && instanciaA !== instanciaB) return false;

  const chamadoA = String(a.chamado_id || a.id_chamado || a.ticket_id || "");
  const chamadoB = String(b.chamado_id || b.id_chamado || b.ticket_id || "");
  if (chamadoA && chamadoB && chamadoA === chamadoB) return true;

  const leadA = String(a.lead_id || a.leadId || a.lead?.id || "");
  const leadB = String(b.lead_id || b.leadId || b.lead?.id || "");
  if (leadA && leadB && leadA === leadB && (!instanciaA || !instanciaB)) return true;

  const telefoneA = normalizarTelefone(a.numero || a.telefone).slice(-11);
  const telefoneB = normalizarTelefone(b.numero || b.telefone).slice(-11);
  if (
    telefoneA &&
    telefoneB &&
    telefoneA === telefoneB &&
    (!instanciaA || !instanciaB || instanciaA === instanciaB)
  ) {
    return true;
  }

  const conversaA = String(a.id || a.conversa_id || a.conversaId || "");
  const conversaB = String(b.id || b.conversa_id || b.conversaId || "");

  return Boolean(conversaA && conversaB && conversaA === conversaB);
}

function ordenarConversasPorChegada(lista = [], aba = "") {
  return [...(Array.isArray(lista) ? lista : [])].sort((a, b) => {
    const dataB = getUltimaAtividadeListaCaptaZap(b, aba);
    const dataA = getUltimaAtividadeListaCaptaZap(a, aba);

    if (dataB !== dataA) return dataB - dataA;

    return (
      getUltimoIdMensagemListaCaptaZap(b) - getUltimoIdMensagemListaCaptaZap(a)
    );
  });
}

function getUltimaAtividadeListaCaptaZap(conversa = {}, aba = "") {
  if (aba === "followup") {
    const liberado = parseTimestamp(
      conversa._follow_up_liberado_em ||
        conversa.followup_liberado_em ||
        conversa.agendado_para ||
        conversa.agendadoPara ||
        conversa.janela_inicio ||
        conversa.proxima_execucao_em ||
        conversa.proximaExecucaoEm ||
        "",
    );

    // Para follow-up, mantém a ordem por vencimento quando existir.
    // Sem vencimento, cai na última atividade normal da conversa.
    if (liberado) return liberado;
  }

  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  const maiorDataMensagem = mensagens.reduce((maior, mensagem) => {
    const timestamp = getMensagemTimestamp(mensagem);
    return timestamp > maior ? timestamp : maior;
  }, 0);

  const dataConversa = parseTimestamp(
    conversa.ultimaMensagemEm ||
      conversa.ultima_mensagem_em ||
      conversa.last_message_at ||
      conversa.data_ultima_mensagem ||
      conversa.datahora ||
      conversa.timestamp ||
      conversa.updated_at ||
      conversa.created_at ||
      conversa.hora ||
      "",
  );

  return Math.max(maiorDataMensagem, dataConversa, 0);
}

function getUltimoIdMensagemListaCaptaZap(conversa = {}) {
  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  const maiorIdMensagem = mensagens.reduce((maior, mensagem) => {
    const id = Number(
      mensagem.id ||
        mensagem.whatsapp_id ||
        mensagem.whatsappId ||
        mensagem.message_id ||
        0,
    );

    return Number.isFinite(id) && id > maior ? id : maior;
  }, 0);

  const idConversa = Number(
    conversa.ultima_mensagem_id ||
      conversa.ultimaMensagemId ||
      conversa.whatsapp_id ||
      conversa.whatsappId ||
      conversa.mensagem_id ||
      conversa.id ||
      0,
  );

  return Math.max(
    maiorIdMensagem,
    Number.isFinite(idConversa) ? idConversa : 0,
  );
}

function getOrdenacaoData(conversa, aba = "") {
  return getUltimaAtividadeListaCaptaZap(conversa, aba);
}

function formatarResumoFollowUpLocal(regras = []) {
  const lista = Array.isArray(regras) ? regras : [];
  const partes = lista
    .map((regra) => {
      const titulo =
        regra?.shortLabel ||
        regra?.titulo ||
        regra?.nome ||
        regra?.label ||
        regra?.mensagem_rapida_tipo ||
        regra?.tipoMensagemRapida ||
        "";
      const prazo =
        regra?.prazoLabel ||
        regra?.prazo_label ||
        regra?.tempo_label ||
        regra?.intervalo_label ||
        "";

      if (titulo && prazo) return `${titulo}: ${prazo}`;
      if (titulo) return titulo;
      if (prazo) return prazo;
      return "";
    })
    .filter(Boolean);

  if (!partes.length) return "";
  return partes.length > 3
    ? `${partes.slice(0, 3).join(" · ")} · +${partes.length - 3}`
    : partes.join(" · ");
}

function normalizarTelefone(valor) {
  return String(valor || "").replace(/\D/g, "");
}

function normalizar(valor) {
  return String(valor || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}